/**
 * ShowPilot widget entry. Compiled to `dist/app.js` as an IIFE via
 * esbuild, served from cdn.showpilot.com/widget/v1/app.js.
 *
 * Embed contract (see docs/RUNTIME_CONTRACT.md):
 *
 *   <script async src="https://cdn.showpilot.com/widget/v1/app.js"></script>
 *   <div data-showpilot-agent="<publicId>"></div>
 *
 * Optional mount attrs:
 *   data-showpilot-layout  = icon | input | expanded | sidebar | full
 *   data-showpilot-mock    = "true"     (use mock stream instead of /chat)
 *   data-showpilot-api     = api base   (override default)
 *   data-showpilot-rail    = dock | overlay  (rail pushes the page aside; default dock)
 *   data-showpilot-beam    = stay            (Beam keeps the full figure; default settles to a bust on scroll/idle)
 *
 * Boot order:
 *   1. find all mount elements (supports >1 on a page)
 *   2. resolve config (publicId + apiBaseUrl + initial layout)
 *   3. restore sessionStorage snapshot if present (forces SIDEBAR per legacy UX)
 *   4. fetch /config to theme the launcher
 *   5. defer /session + /chat until the user sends a message
 */

import type { ChatEvent, ChatHistoryMessage } from '@showpilot/core';
import {
  resolveConfig,
  PUBLIC_ID_ATTRS,
  applyEnvOverrideFromUrl,
  getApiBaseOverride,
  isInternalMode,
  readAgentPreviewToken,
  detectAssetBaseUrl,
  setBackendOverride,
  exitInternalMode,
  SP_DEV_API_BASE,
  type WidgetConfig,
} from './config';
import { applyWidgetChannelFromUrl, maybeLoadWidgetChannel } from './dev-widget';
import { preloadVoiceRuntime, setDailyChunkBase } from './voice/daily-loader';
import { setFormsChunkBase } from './forms-loader';
import { consumeHashAction, installActionClicks, installHashActionListener } from './action-detect';
import { loadActions, setActionsChunkBase } from './actions-loader';
import { setCursorChunkBase } from './cursor-loader';
import type { ActionRunner, ActionSource } from './action-runner';
import { setBoundlessChunkBase } from './boundless-loader';
import { installErrorReporter } from './error-report';
import {
  mountOfferToast,
  mountOfferInterstitial,
  mountOfferBanner,
  stripInlineMarkup,
} from './components/offer-surfaces';
import {
  recordProactiveShown,
  readProactiveShownCount,
  PROACTIVE_SESSION_BUDGET,
} from './components/pilot-prompt';
import { capLayoutForMobile, isLayout, LAYOUTS, RESUME_LAYOUT } from './layouts';
import { railModeFromAttr } from './host-dock';
import { rebaseToVisitorOrigin } from './nav';
import { parsePageTouch, type PageTouch } from './page-context';
import { deviceSig } from './viewport';
import { loadSession, saveSession, setSessionScope, type SessionSnapshot, pendingMessageSendable } from './session';
import {
  createSession,
  postActionFire,
  discoverAgentForHost,
  fetchConfig,
  isPlanRefusal,
  postDecide,
  postPresenceEvent,
  postBookSlot,
  fetchMeetingOptions,
  postRouteReadiness,
  postSessionEvent,
  speakText,
  submitJourneyAnswer,
  unlockExperience,
} from './api';
import { streamChat } from './sse';
import { CHAT_RETRY_DELAY_MS, shouldRetryTurn } from './chat-retry';
import { scrapePageElements } from './page-scraper';
import { getOrCreateVisitorFingerprint } from './visitor-id';
import { bindPageSdk, getPageContext, installPageSdk, requestAction, setMountOnDemand } from './page-sdk';
import { startVisitorHeartbeat } from './visitor-ping';
import { mockStream } from './mock';
import { startVoice, type VoiceBootstrapHandle } from './voice/bootstrap';
import { routeTypedMessage } from './voice/typed-in-call';
import { createPresenterStage } from './presenter-stage';
import { createQuietNudge } from './quiet-nudge';
import { createStageTruth } from './stage-truth';
import { createTourEndRetake, TOUR_END_NUDGE } from './tour-end-retake';
import {
  notifyVoiceOfMediaEnded,
  notifyVoiceOfScreenChange,
  notifyVoiceOfTourEnded,
  setPlayoutLogListener,
  notifyVoiceOfPageChange,
  notifyVoiceOfVisitorAction,
  notifyVoiceOfVisitorText,
  pushVoicePageContext,
  type RejoinRoom,
} from './voice/daily-session';
import { mountPreactIsland } from './voice/preact-mount';
import { VoiceStatusBadge } from './voice/components/VoiceStatusBadge';
import { voiceState, voiceTransition, botVideoStream } from './voice/voice-state';
import { arrivalOfferAllowed, proactiveTickAllowed, voiceIsLive } from './proactive-gate';
import { initMediaFloor } from './media-floor';
import { subscribeVoiceEvents, type VoiceEventSubscription } from './voice/data-plane';
import { installMobileFloor } from './mobile-floor';
import { mountPilotPrompt } from './components/pilot-prompt';
import { setSpeechChunkBase } from './speech-loader';
import { installTriggerWatchers, type WatcherTrigger } from './trigger-watcher';
import { installMomentWatchers, type Moment } from './moment-watcher';
import { installVisitLog, visitPageChanged, visitSnapshot } from './visit-log';
import type { ArrivalOffer } from './api';
import { mountWidget, type WidgetUI } from './ui';
import { beamPresenceFor, readBeamPresence, sessionStorageOrNull } from './beam-presence';
import { pushMilestone } from './data-layer';
import { loadProject, loadTours, setToursChunkBase } from './tours/tours-loader';
import { transformLegacyTours } from './tours/legacy-compat';
export const WIDGET_VERSION = '0.2.0';

/**
 * Offer CTA action (Slice O1) — mirrors `OfferCtaAction` in @showpilot/db.
 * Defined locally because the widget bundle must not import server DB types.
 */
type OfferCtaAction =
  | { type: 'none' }
  | { type: 'book_meeting' }
  | { type: 'capture_lead' }
  | { type: 'navigate'; url: string }
  | { type: 'open_content'; contentRefId: string };

/**
 * Mount selector union — finds elements carrying either the canonical
 * v3 `data-showpilot-agent` attr or any of the legacy attribute names
 * still deployed on customer sites. See `config.ts > PUBLIC_ID_ATTRS`.
 */
const MOUNT_SELECTOR = PUBLIC_ID_ATTRS.map((a) => `[${a}]`).join(',');

/**
 * Offers v2 — the LIVE offer deploy matched for this page arrival.
 * The discover handler stashes it (after client-side utm evaluation)
 * and boot() presents it once /config resolves (the payload needs the
 * agent name + theme). One-shot per page load.
 */
let pendingArrivalOffer: ArrivalOffer | null = null;
/** True when an arrival interstitial presented this page load — its
 *  tie-back greeting outranks the decide loop's generic rewrite. */
let arrivalOfferOwnsGreeting = false;
/** v3 — identified-visitor hint riding the discover response (no PII;
 *  first name + a flag). Offer surfaces skip the email field and greet
 *  by name. */
let offerIdentity: { identified: true; firstName?: string } | null = null;
/** v3 — an accepted open_experience offer, consumed by the NEXT chat
 *  message's context so the runtime launches the experience
 *  deterministically before the model turn. One-shot. */
let pendingExperienceLaunch: { experienceId: string; offerId: string } | null = null;
/** An accepted non-experience offer (open_content / book_meeting /
 *  capture_lead / none) — rides the next chat message's context as
 *  `launchOfferId` alone, so the runtime can ground the first turn in
 *  the offer's copy and open an open_content item deterministically.
 *  One-shot. */
let pendingOfferAccept: { offerId: string } | null = null;
// Deterministic capture POST, assigned during boot (QW-2). The offer
// interstitial fires it directly so a typed email persists even when
// the agent never calls a capture tool.
let contactCapturePost:
  | ((fields: Record<string, string>, intent?: 'contact' | 'callback' | 'booking') => Promise<void>)
  | null = null;
/** The deployment (placement) whose trigger mounted this page's widget —
 *  stamped when a discover trigger fires, echoed on presence events so
 *  the shown→accepted funnel attributes per placement (D1). Null for
 *  attribute mounts and legacy discover payloads. */
let activeDeploymentId: string | null = null;

async function boot(mount: HTMLElement): Promise<void> {
  let config: WidgetConfig;
  try {
    config = resolveConfig(mount);
  } catch (err) {
    console.error(err);
    return;
  }

  // Mobile UX floor (slice 2 / M1) — viewport meta tweaks + iOS
  // visualViewport keyboard tracking. Idempotent; subsequent calls
  // (multiple widget mounts on the same page) are no-ops.
  installMobileFloor();

  // (The Personalize-v1 console hooks — window.__showpilotPilotPrompt and
  // friends — are gone: the /decide wiring that replaced them shipped long
  // ago, and every surface is reachable through the product paths below.)

  // A tour / hub door is its own conversation: scope the resumable
  // session to the door so tour B never opens on tour A's transcript.
  // Customer sites pass no scope — continuity across their pages stays.
  if (typeof window !== 'undefined' && window.SHOWPILOT_CONFIG?.tourPage) {
    setSessionScope(config.presenterTourPublicId ?? config.projectPublicId ?? null);
  }
  const snapshot = loadSession(config.publicId);
  const hasResume = snapshot.history.length > 0;
  // Resume fidelity: restore the layout the visitor actually had open
  // (minimized stays minimized, rail stays rail). RESUME_LAYOUT is the
  // fallback for legacy snapshots without one.
  const intendedLayout = hasResume
    ? isLayout(snapshot.layout)
      ? snapshot.layout
      : RESUME_LAYOUT
    : config.layout;

  // Mobile takeover guard: on phones, downgrade takeover layouts to
  // INPUT (Site Agent) so the host page stays visible. We hold onto
  // intendedLayout so we can restore it after /config arrives if the
  // agent has explicitly opted in via mobileTakeoverAllowed.
  const initialLayout = capLayoutForMobile(intendedLayout);
  const cappedFromTakeover = initialLayout !== intendedLayout;

  const ui = mountWidget(mount, {
    initialLayout,
    railMode: railModeFromAttr(mount.getAttribute('data-showpilot-rail')),
    assetBaseUrl: config.assetBaseUrl,
    presentDoor: config.presentDoor,
  });
  // Action Links arriving before the runner exists (SDK calls queued
  // pre-load, the deep link, an early click) wait here; the eager session
  // create below steps aside for them so the Action rides the create.
  const earlyActions: Array<[string, ActionSource]> = [];
  // An Action owns this page load's opening: the proactive /decide stands
  // down (no composed greeting competing with the task) and the static
  // welcome stays unpainted when the conversation has not started yet.
  let actionEngaged = false;
  let onAction: (id: string, source: ActionSource) => void = (id, source) => {
    actionEngaged = true;
    earlyActions.push([id, source]);
  };
  // A plain "open the agent" (page SDK, an Action the runner could not load):
  // phones get the conversation sheet, never the expanded greeter stage
  // (phone sweep 2026-10-20, M-11); larger screens keep the classic panel.
  const openConversation = (): void =>
    ui.setLayout(deviceSig.value === 'phone' ? LAYOUTS.SIDEBAR : LAYOUTS.EXPANDED);
  // Page SDK: `window.showpilot` now has a mount to act on. Last mount wins.
  bindPageSdk({
    publicId: config.publicId,
    apiBaseUrl: config.apiBaseUrl,
    fingerprint: getOrCreateVisitorFingerprint,
    openPanel: openConversation,
    openTour: (id, step) => {
      if (window.__showpilotTour) window.__showpilotTour.open(id);
      else ui.openPresenterTour(id);
      if (typeof step === 'number') setTimeout(() => window.__spTourControl?.goTo(step), 800);
    },
    openAction: (id, source) => onAction(id, source),
  });
  // Directed Experience (P2): suppress the static inbound welcome BEFORE
  // /config arrives, so setConfig never sets "how can I help?" — the agent
  // leads with its own directed opening. Persistent flag, not a late
  // clear, so it can't lose a timing race with config load.
  if (config.experience) {
    ui.clearWelcome();
    // The experience IS the whole page — hide minimize/close so the
    // visitor can't collapse it to a chat button over a blank page.
    ui.setStandaloneExperience(true);
  }
  // Every snapshot now records the live layout so cross-page resume
  // restores exactly what the visitor had open.
  currentLayoutForPersist = () => ui.getLayout();
  // Presence anchor (embed attr): bottom-center variant for the
  // launcher + start bar; default stays bottom-right.
  if (config.position === 'center') ui.root.classList.add('sp-pos-center');
  // Lazy Daily SDK chunk lives next to app.js (script dir, never the
  // asset override) — configure before any voice/relay path can run.
  setDailyChunkBase(config.scriptBaseUrl);
  setBoundlessChunkBase(config.scriptBaseUrl);
  setSpeechChunkBase(config.scriptBaseUrl);
  setToursChunkBase(config.scriptBaseUrl);
  setFormsChunkBase(config.scriptBaseUrl);
  setActionsChunkBase(config.scriptBaseUrl);
  setCursorChunkBase(config.scriptBaseUrl);
  // Crash reports for OUR bundle only (stack-filtered) — the sole error
  // visibility we have on customer sites. See error-report.ts.
  installErrorReporter({
    apiBaseUrl: config.apiBaseUrl,
    scriptBaseUrl: config.scriptBaseUrl,
    agent: config.publicId,
  });
  ui.renderHistory(snapshot.history);

  // Resume-after-nav greeter flash: when the visitor was mid voice-call on
  // the previous page, voice auto-reopens here — but asynchronously. In the
  // gap before it reconnects, the pre-recorded greeter clip would flash
  // before the live avatar re-binds. The greeter-hide rule is gated on
  // `.sp-voice-mode`, so apply that class up front; the async voice reopen
  // re-applies it idempotently, and the live avatar takes over on bind.
  if (hasResume && snapshot.voiceModeActive) {
    ui.root.classList.add('sp-voice-mode');
  }

  // Cross-page highlight handoff. When the agent's `highlightElement`
  // tool fires with a different URL than the visitor's current page,
  // the widget calls this handler instead of trying to highlight an
  // element that doesn't exist on the current DOM. We persist the
  // highlight to sessionStorage and trigger a same-origin navigation;
  // the next-page widget picks it up via the `pendingHighlight` block
  // below.
  // Agent-driven navigation unloads the page mid-turn — snapshot the
  // conversation (history so far + live layout via persist's getter)
  // synchronously first, or the next page cold-mounts with amnesia.
  // When the AGENT chose the destination, queue a hidden arrival direction
  // for the next page: "Taking you there now." followed by silence was the
  // Phase E buyer-run finding — the page is the stage, so the agent speaks
  // on arrival (one line about what is here, then the thread continues).
  ui.onBeforeHostNavigate((next) => {
    persist({
      publicId: config.publicId,
      sessionId,
      history,
      ...(next && sessionId
        ? { pendingMessage: { text: arrivalDirection(next.pathname), queuedAt: Date.now(), hidden: true } }
        : {}),
    });
  });

  ui.onHighlightNavigate(({ text, reason, url }) => {
    // Preserve voiceModeActive across the nav. Without this, the
    // persist call below silently overwrites the flag with undefined,
    // and the next-page widget reads voiceModeActive=false → does NOT
    // auto-reopen voice → audio dies on the new page even though the
    // visitor was mid-call. Read directly from the voice state machine
    // since `voiceHandle` is in TDZ-safe closure scope (declared later
    // in this fn but always initialized to null before any handler fires).
    persist({
      publicId: config.publicId,
      sessionId,
      history,
      pendingHighlight: { text, reason, url, queuedAt: Date.now() },
      voiceModeActive: voiceState.value.name !== 'idle',
    });
    try {
      const target = new URL(url, window.location.href);
      if (target.origin === window.location.origin) {
        window.location.href = target.href;
      }
    } catch {
      /* malformed url — ignore */
    }
  });

  // Replay any pending highlight queued by a previous-page visit. Wait
  // ~500ms after mount so the host page's own scripts (analytics,
  // sticky headers, lazy-loaded sections) have a chance to settle —
  // legacy mirror: `agent-chat-v2/index.vue:864`.
  if (snapshot.pendingHighlight) {
    const ph = snapshot.pendingHighlight;
    setTimeout(() => {
      ui.applyPendingHighlight({ text: ph.text, reason: ph.reason, url: ph.url });
    }, 500);
    // Clear pendingHighlight so a refresh doesn't re-trigger, but
    // preserve voiceModeActive so the WS-reopen path below still
    // sees an accurate flag if it reads the just-saved snapshot.
    persist({
      publicId: config.publicId,
      sessionId: snapshot.sessionId,
      history: snapshot.history,
      pendingHighlight: undefined,
      voiceModeActive: snapshot.voiceModeActive,
    });
  }

  let welcomeMessage = '';
  let voiceEnabled = false;
  let remoteConfig: Awaited<ReturnType<typeof fetchConfig>> | null = null;
  try {
    const remote = await fetchConfig(
      config.apiBaseUrl,
      config.publicId,
      undefined,
      config.agentPreviewToken,
    );
    remoteConfig = remote;
    ui.setConfig(remote);
    // GTM bridge: the agent is configured + visible on this page —
    // the impression denominator for engagement-rate math.
    pushMilestone('agent_shown', { agent: remote.agent.name });
    welcomeMessage = remote.agent.welcomeMessage ?? '';
    voiceEnabled = remote.voice?.enabled ?? false;
    // Warm the voice runtime once the page has settled (daily-loader.ts).
    if (voiceEnabled && !config.mock) preloadVoiceRuntime();
    // Switch to the agent's default view ONLY when the mount didn't
    // already carry one AND the visitor isn't already mid-conversation.
    // Trigger-driven mounts (autoMount via /v1/discover) set
    // data-showpilot-layout from trigger.view — that's a deployment-
    // level choice and must NOT be overridden by agent.view. Bare
    // customer embeds (data-showpilot-agent with no layout) fall
    // through to agent.view as the per-agent default. On resume
    // (sessionStorage has chat history from a prior page in the same
    // visit), the visitor is already past the cold-open state — they
    // were chatting in rail/sidebar before nav, so don't yank them
    // back to engage as if it were a first touch.
    const attrView =
      config.viewFromAttr && config.viewFromAttr in VIEW_BOOT_LAYOUTS
        ? (config.viewFromAttr as Parameters<typeof ui.setView>[0])
        : null;
    // PRESENCE-WINS (Beam): she stood on stage earlier in this visit and
    // the server still says this agent serves Beam → she stays on stage
    // here, whatever this page's own rule, resume state or attr say.
    const beamPresence =
      remote.voice?.presentation === 'boundless' &&
      beamPresenceFor(sessionStorageOrNull(), remote.agent.publicId);
    if (beamPresence) {
      ui.setView('unbound');
    } else if (attrView && !hasResume) {
      // Discover-driven mount authored a deployment view — it wins over
      // agent.view (the per-agent default), same precedence the layout
      // attr already had.
      ui.setView(attrView);
    } else if (!config.layoutFromAttr && !hasResume) {
      ui.setView(remote.agent.view);
    }
    // Honor the per-agent opt-in for mobile takeover. If we capped the
    // initial layout but this agent is configured as a landing-page
    // experience (mobileTakeoverAllowed=true), expand back to what
    // they originally requested. Brief flash from INPUT → takeover is
    // accepted as the cost of the safe default.
    if (cappedFromTakeover && remote.agent.mobileTakeoverAllowed) {
      ui.setLayout(intendedLayout);
    }
  } catch (err) {
    // A plan refusal (trial ended, agent line off) means: render nothing.
    if (isPlanRefusal(err)) {
      mount.hidden = true;
      return;
    }
    // Any other config fetch failure is non-fatal — the widget still opens
    // with defaults so local dev works before the chat-api is reachable.
    if (!config.mock) {
      console.warn('[showpilot] /config fetch failed', err);
    }
  }

  // Visitor heartbeat — bumps visit_count + total_engaged_secs even on
  // bounce-without-chat sessions. Fingerprint is always defined (DNT
  // falls back to the non-storage browser-hash); the visitor-id module
  // handles the privacy gate.
  if (!config.mock) {
    startVisitorHeartbeat({
      apiBaseUrl: config.apiBaseUrl,
      publicId: config.publicId,
      fingerprint: getOrCreateVisitorFingerprint(),
    });
    // M8 metric ladder — forward the UI's pre-session presence funnel
    // (shown / accepted-per-act / dismissed) to /v1/presence/events.
    // Boot events fired before this registration are replayed from the
    // UI's queue. Device class from the root's data-device stamp.
    ui.onPresence((e) => {
      const device = ui.root.getAttribute('data-device');
      postPresenceEvent(config.apiBaseUrl, {
        publicId: config.publicId,
        fingerprint: getOrCreateVisitorFingerprint(),
        type: `presence-${e.type}`,
        surface: e.surface,
        ...(e.act ? { act: e.act } : {}),
        ...(device === 'phone' || device === 'tablet' || device === 'desktop' ? { device } : {}),
        ...(activeDeploymentId ? { deploymentId: activeDeploymentId } : {}),
        pageUrl: window.location.href.slice(0, 2000),
      });
    });
  }

  // Offers v2 — present the LIVE offer deploy matched for this arrival.
  // Interstitial weighs the WHOLE proactive budget (the one sanctioned
  // takeover — the visitor clicked into the campaign); toast weighs 1.
  // While an arrival interstitial is up, every widget presence yields
  // (sp-offer-up) — dismissing falls to the granted presence, which is
  // already mounted underneath.
  // V3-D pacing: an arrival offer OWNS the page load's proactive act —
  // when one will present, the decide loop stands down entirely (no
  // stacked prompt, no wasted LLM call). One system, one budget.
  let arrivalOfferPlanned = false;
  // Never over a live conversation (David 2026-10-26): a chat that
  // resumed across this navigation, or a live / resuming call, owns the
  // page; the campaign offer stands down. See arrivalOfferAllowed.
  const offerOverConversation =
    pendingArrivalOffer !== null &&
    !arrivalOfferAllowed({
      conversationResumed: hasResume,
      voiceLive: voiceIsLive(voiceState.value.name, hasResume && snapshot.voiceModeActive === true),
    });
  if (offerOverConversation) {
    console.info('[showpilot] arrival offer held — a conversation is already live on this page');
    pendingArrivalOffer = null;
  }
  if (pendingArrivalOffer && !config.mock) {
    const arrival = pendingArrivalOffer;
    pendingArrivalOffer = null;
    arrivalOfferOwnsGreeting = arrival.presentation === 'interstitial';
    arrivalOfferPlanned =
      arrival.presentation === 'interstitial' ||
      readProactiveShownCount() < PROACTIVE_SESSION_BUDGET;
    const offer = arrival.offer;
    const design = arrival.design;
    // Offers v2 funnel — shown / accepted / dismissed per offerId, same
    // pre-session pipe as the presence ladder.
    const offerEvent = (
      type: 'offer-shown' | 'offer-accepted' | 'offer-declined' | 'offer-dismissed',
    ): void =>
      postPresenceEvent(config.apiBaseUrl, {
        publicId: config.publicId,
        fingerprint: getOrCreateVisitorFingerprint(),
        type,
        offerId: offer.id,
        presentation: arrival.presentation,
        pageUrl: window.location.href.slice(0, 2000),
      });
    const payload = {
      key: `arrival-${offer.id}`,
      agentName: remoteConfig?.agent.name ?? 'Pilot',
      avatarUrl: remoteConfig?.theme.avatarUrl ?? null,
      imageUrl: offer.imageUrl ?? design?.background?.imageUrl ?? null,
      headline: offer.headline,
      message: offer.message,
      ctaLabel: offer.ctaLabel,
      primaryColor: remoteConfig?.theme.primaryColor,
      design,
      ...(offerIdentity ?? {}),
    };
    const onAccept = (email?: string): void => {
      const action = offer.ctaAction;
      // Same routing as the O1 toast: navigate dispatches the nav
      // event; everything else morphs into chat so the agent owns
      // the booking / capture / content surface.
      if (action.type === 'navigate' && action.url) {
        ui.handleChatEvent({ type: 'navigate-url', payload: { url: action.url } });
        return;
      }
      // The server must know WHAT was accepted: seed the offer copy into
      // the LLM history as the agent's prior turn (the visible bubble is
      // painted by submitFromPrompt; this is the server-side twin), and
      // ride the offer id on the first message's context. Without both,
      // the model's whole first turn was the bare CTA label ("Read the
      // guide") — no idea which guide, or that an offer existed at all.
      proactiveGreeting = stripInlineMarkup(offer.message);
      // v3 — the offer is the poster, the experience is the show. The
      // launch rides the first message's context; the runtime opens the
      // experience deterministically and the agent narrates entry.
      if (action.type === 'open_experience') {
        pendingExperienceLaunch = { experienceId: action.experienceId, offerId: offer.id };
        ui.submitFromPrompt({
          precedingAssistantMessage: stripInlineMarkup(offer.message),
          userText: offer.ctaLabel ?? 'Show me.',
        });
        return;
      }
      pendingOfferAccept = { offerId: offer.id };
      // QW-2: a typed email is a captured lead the moment it's typed —
      // POST it deterministically instead of smuggling it as chat prose
      // and hoping the model calls a capture tool. The chat beat still
      // runs so the agent owns the follow-up conversation.
      if (action.type === 'capture_lead' && email) {
        void contactCapturePost?.({ email }).catch(() => {
          /* capture is best-effort here; the chat message still carries it */
        });
      }
      const userText =
        action.type === 'book_meeting'
          ? "I'd like to book a time."
          : action.type === 'capture_lead'
            ? email
              ? `I'd like to get in touch — my email is ${email}.`
              : "I'd like to get in touch."
            : (offer.ctaLabel ?? 'Tell me more.');
      ui.submitFromPrompt({
        precedingAssistantMessage: stripInlineMarkup(offer.message),
        userText,
      });
    };
    const present = (): void => {
      if (arrival.presentation === 'interstitial') {
        recordProactiveShown();
        recordProactiveShown(); // O5 — interstitial weighs 2 (the whole budget)
        // Agent tie-back — the presence underneath speaks the offer
        // instead of greeting cold. Must commit BEFORE the welcome
        // bubble paints (P2-strict never swaps after paint), so it
        // happens at present time, not dismiss time.
        ui.setEngageGreeting(stripInlineMarkup(offer.message));
        // The page is the offer's — every widget presence yields until
        // the visitor accepts or dismisses (the engage panel was booting
        // ALONGSIDE the takeover in David's 2026-07-08 test).
        ui.root.classList.add('sp-offer-up');
        const release = (): void => ui.root.classList.remove('sp-offer-up');
        offerEvent('offer-shown');
        mountOfferInterstitial(
          payload,
          {
            onAccept: (email) => {
              release();
              offerEvent('offer-accepted');
              onAccept(email);
            },
            onDismiss: () => {
              release();
              offerEvent('offer-dismissed');
              // Thread the offer into the LLM's opening context so a
              // later conversation start knows what was declined.
              proactiveGreeting = stripInlineMarkup(offer.message);
            },
          },
          { captureEmail: offer.captureEmail },
        );
        return;
      }
      if (readProactiveShownCount() >= PROACTIVE_SESSION_BUDGET) return;
      recordProactiveShown();
      offerEvent('offer-shown');
      if (arrival.presentation === 'banner') {
        mountOfferBanner(payload, {
          onAccept: () => {
            offerEvent('offer-accepted');
            onAccept();
          },
          onDismiss: () => offerEvent('offer-dismissed'),
        });
        return;
      }
      mountOfferToast(payload, {
        onAccept: () => {
          offerEvent('offer-accepted');
          onAccept();
        },
        // Explicit "No, thanks" — first-class decline, richer than the ×.
        onDecline: () => offerEvent('offer-declined'),
        onCollapse: () => {
          offerEvent('offer-dismissed');
          ui.setOfferBadge(1);
        },
      });
    };
    const delayMs = (arrival.conditions?.afterSeconds ?? 0) * 1000;
    if (delayMs > 0) setTimeout(present, delayMs);
    else present();
  }

  // Personalize v1 — POST /v1/decide and mount Pilot Prompt if a
  // prompt-surface action returns. Server short-circuits to 204 when
  // the org doesn't have the flag, so this call is a no-op for the
  // vast majority of customers + costs nothing on the host page.
  //
  // The heartbeat above just upserted the visitor row by fingerprint,
  // so by the time /decide runs the row exists with current visit_count
  // and any Big Picture enrichment is in-flight. (First-visit account
  // tokens will likely miss + fall back to messageWhenMissing; that's
  // the design — better silent than embarrassing.)
  //
  // Tied to the live widget surface, so mock mode skips. SPA nav
  // re-trigger lands in Slice 5 — for v1 we fire on initial boot only.
  // Experience mode skips the proactive decide loop entirely: the seed
  // (below) drives the opening, so a /decide greeting would just compete.
  // An arrival offer also stands decide down (V3-D — one proactive act
  // per page load; the offer is it).
  if (!config.mock && !config.experience && !arrivalOfferPlanned) {
    const fingerprint = getOrCreateVisitorFingerprint();
    const decideView = remoteConfig?.agent.view;
    // D3-as-engage REVERTED (David, 2026-07-07): the Card presented the
    // engage first touch on desktop but shipped without voice entry,
    // close, or the chirp/decide swap — a worse copy of the mature
    // engage panel ("Engage was better designed"). Engage boots its
    // panel again; the Card remains the OFFER/proactive teaser surface
    // (plain mountPilotPrompt paths below).
    const firePostDecide = (moment?: Moment): void => {
      // One voice per page load: an engaged Action is it, and so is a
      // live voice call (the bot speaks; bubble rewrites are invisible
      // with the panel open) — see proactive-gate.ts.
      if (
        !proactiveTickAllowed({
          actionEngaged,
          voiceLive: voiceIsLive(voiceState.value.name, hasResume && snapshot.voiceModeActive === true),
        })
      )
        return;
      void postDecide(
        config.apiBaseUrl,
        {
          publicId: config.publicId,
          fingerprint,
          pageUrl: window.location.href,
          // Pass view so the LLM knows the rendering surface. `opening`
          // marks a boot page-view on a pre-conversation surface (ANY
          // view — engage included: its composed opening becomes/follows
          // the welcome bubble) — the server then constrains the tool
          // enum so compose_opening owns the moment (a resumed thread is
          // a conversation, not an opening).
          context: {
            ...readPageContext(),
            view: decideView,
            // A moment tick is never the opening: the opening already happened.
            opening: !hasResume && !moment,
            ...(moment ? { moment } : {}),
          },
        },
        config.agentPreviewToken,
      ).then(handleDecision);
    };
    let arrivalActionTried = false;
    const handleDecision = (decision: Awaited<ReturnType<typeof postDecide>>): void => {
      // A decide that resolved after an Action fired must not greet over it
      // — and a click Action outranks an arrival match.
      if (actionEngaged) return;
      if (!decision) return;
      // Action Links (D4) — the server matched an arrival rule on the
      // landing utm / referrer. One per page load: the boot decide is the
      // arrival; an SPA nav keeps document.referrer and must not re-open.
      if (decision.toolCall.name === 'open_action') {
        const actionId = (decision.toolCall.args as { actionId?: unknown }).actionId;
        if (arrivalActionTried || typeof actionId !== 'string') return;
        arrivalActionTried = true;
        requestAction(actionId, 'arrival');
        return;
      }
      // Opening Turn — the composed cold/revisit open for pre-conversation
      // surfaces (site-agent / rail / chat-button). The static greeter
      // painted at boot; this appends the personalized follow-up bubble
      // + intent chips + ghost suggestion. ui.applyComposedOpening
      // refuses once a conversation exists (incl. resumed sessions).
      if (decision.toolCall.name === 'compose_opening') {
        // A campaign arrival offer IS this page load's personalization.
        if (arrivalOfferOwnsGreeting) return;
        const args = decision.toolCall.args as {
          message?: string;
          chips?: unknown;
          ghostSuggestion?: string;
        };
        const message = typeof args.message === 'string' ? args.message.trim() : '';
        if (!message) return;
        const chips = Array.isArray(args.chips)
          ? args.chips.filter((c): c is string => typeof c === 'string')
          : [];
        const applied = ui.applyComposedOpening({
          message,
          chips,
          ghostSuggestion: typeof args.ghostSuggestion === 'string' ? args.ghostSuggestion : null,
        });
        // Thread the opening into the first /chat turn as the prior
        // assistant message (P8 pattern) so the runtime knows what it
        // opened with and never re-greets.
        if (applied) proactiveGreeting = message;
        return;
      }
      // Slice 6.6: engage greeter rewrite. When the agent is in engage
      // view, the proactive LLM can override the static welcomeMessage
      // with a per-visitor one. Window: ~1800ms before the static
      // welcome chirps in. Beyond that, no-op (visitor already saw it).
      if (decision.toolCall.name === 'update_engage_greeting') {
        if (decideView !== 'engage') return;
        // A campaign arrival offer IS this page load's personalization —
        // its tie-back greeting outranks the generic decide rewrite.
        if (arrivalOfferOwnsGreeting) return;
        const args = decision.toolCall.args as { message?: string };
        const message = typeof args.message === 'string' ? args.message.trim() : '';
        if (!message) return;
        // P2-strict (behind ui.mobileV2): setEngageGreeting refuses a
        // post-paint swap. Either way the personalized copy threads into
        // the first /chat turn as the prior assistant message (P8) — a
        // late decide OWNS the conversation opening instead of rewriting
        // visible text, and the runtime never re-greets.
        ui.setEngageGreeting(message);
        proactiveGreeting = message;
        return;
      }

      // M9 — proactive dispatchers (behind ui.mobileV2).
      if (ui.isMobileV2() && decision.toolCall.name === 'open_content') {
        // P4 — teaser-first, takeover-capped: never auto-open FULL. Render
        // a Card whose CTA morphs into chat, where the agent's open_content
        // tool owns the actual viewer (same contract as the offer CTA).
        const args = decision.toolCall.args as { contentId?: string; message?: string };
        const msg =
          typeof args.message === 'string' && args.message.trim()
            ? args.message.trim()
            : 'I queued something worth a look — want to see it?';
        mountPilotPrompt(
          {
            key: decision.decisionId,
            agentName: remoteConfig?.agent.name ?? 'Pilot',
            avatarUrl: remoteConfig?.theme.avatarUrl ?? null,
            message: msg,
            cta: { label: 'Take a look' },
            primaryColor: remoteConfig?.theme.primaryColor,
          },
          {
            onSubmit: (value) =>
              ui.submitFromPrompt({ precedingAssistantMessage: msg, userText: value }),
            onCta: () =>
              ui.submitFromPrompt({
                precedingAssistantMessage: msg,
                userText: 'Yes — show me.',
              }),
            onDismiss: () => {},
          },
          {
            mobileV2: true,
            greeterFocalPoint: remoteConfig?.voice.greeterFocalPoint,
          },
        );
        return;
      }
      if (ui.isMobileV2() && decision.toolCall.name === 'highlight_element') {
        // P5 — composes with the co-pilot: highlight the on-page element;
        // in-session the PiP caption narrates, out-of-session a teaser
        // explains why the page just lit up.
        const args = decision.toolCall.args as { targetText?: string; message?: string };
        const target = typeof args.targetText === 'string' ? args.targetText.trim() : '';
        if (!target) return;
        ui.handleChatEvent({ type: 'highlight-element', payload: { text: target } });
        ui.setSpeechCaption(
          typeof args.message === 'string' && args.message.trim()
            ? args.message.trim()
            : 'Guiding — take a look at the highlight.',
        );
        return;
      }

      // Slice 6 contract: the LLM picks a tool by name. wait/suppress
      // never arrive here (server 204).
      if (decision.toolCall.name !== 'propose_pilot_prompt') return;

      // Suppression. P3 (behind ui.mobileV2): surface-CREATING acts fire
      // only when the widget is dormant at altitude 0/1 — ICON (docked)
      // or INPUT (co-present pill). An open conversation, sheet, rail, or
      // content view suppresses; message acts (greeting/continuity) are
      // not gated here. Legacy rule (flag off): suppress by VIEW NAME.
      if (ui.isMobileV2()) {
        const layout = ui.getLayout();
        if (layout !== 'icon' && layout !== 'input') return;
      } else if (decideView === 'engage' || decideView === 'chat-button' || decideView === 'rail') {
        return;
      }

      const args = decision.toolCall.args as {
        message?: string;
        chips?: string[] | string;
        // Slice O2 — marketing-asset offer: optional hero image + headline.
        imageUrl?: string | null;
        headline?: string | null;
        // Slice O1 — Offer CTA: a label + a bound action the host dispatches.
        cta?: { label?: string; action?: OfferCtaAction } | null;
      };
      const message = typeof args.message === 'string' ? args.message : '';
      if (!message) return;
      // Beam on stage: she says the line herself. A Pilot Prompt card
      // beside the standing agent read as "two widgets" (David
      // 2026-10-20); the CTA/chips fold into the conversation she opens.
      if (ui.presenceSay(message)) {
        proactiveGreeting = message;
        return;
      }
      const imageUrl =
        typeof args.imageUrl === 'string' && args.imageUrl.trim() ? args.imageUrl.trim() : null;
      const headline =
        typeof args.headline === 'string' && args.headline.trim() ? args.headline.trim() : null;
      // Tolerate the LLM emitting chips as a single string or array.
      let chips = Array.isArray(args.chips)
        ? args.chips.filter((c): c is string => typeof c === 'string')
        : typeof args.chips === 'string' && args.chips.trim()
          ? [args.chips]
          : undefined;

      // Offer CTA (Slice O1) — render a primary button when the offer carries
      // one. `navigate` dispatches the same same-origin nav event as the agent;
      // `book_meeting`/`open_content` morph into chat so the agent owns the
      // actual booking/content offer (per the proactive system contract).
      const ctaLabel =
        args.cta && typeof args.cta.label === 'string' && args.cta.label.trim()
          ? args.cta.label.trim()
          : null;
      const ctaAction = args.cta?.action ?? null;
      // P10 (behind ui.mobileV2) — one act, one action: offer-led renders
      // the CTA only (no competing chips); question-led keeps chips. The
      // reply pill is the ever-present morph affordance, not an act.
      if (ui.isMobileV2() && args.cta && typeof args.cta.label === 'string') {
        chips = undefined;
      }
      const onCta = ctaLabel
        ? (): void => {
            if (ctaAction?.type === 'navigate' && ctaAction.url) {
              ui.handleChatEvent({ type: 'navigate-url', payload: { url: ctaAction.url } });
              return;
            }
            // book_meeting / capture_lead / open_content → morph into chat so
            // the agent owns the booking, contact capture, or content surface.
            const userText =
              ctaAction?.type === 'book_meeting'
                ? "I'd like to book a time."
                : ctaAction?.type === 'capture_lead'
                  ? "I'd like to get in touch — here are my details."
                  : ctaLabel;
            ui.submitFromPrompt({ precedingAssistantMessage: message, userText });
          }
        : undefined;

      // O1 — the decisioner picked the TOAST surface (offer-led, mid-
      // session). Explicit decline is a first-class outcome; collapse
      // keeps the offer reachable as a launcher badge (budget cost 0).
      const surface = (decision.toolCall.args as { surface?: string }).surface;
      if (ui.isMobileV2() && surface === 'toast') {
        if (readProactiveShownCount() >= PROACTIVE_SESSION_BUDGET) return;
        recordProactiveShown(); // O5 — toast weighs 1
        mountOfferToast(
          {
            key: decision.decisionId,
            agentName: remoteConfig?.agent.name ?? 'Pilot',
            avatarUrl: remoteConfig?.theme.avatarUrl ?? null,
            imageUrl,
            headline,
            message,
            ctaLabel,
            primaryColor: remoteConfig?.theme.primaryColor,
          },
          {
            onAccept: () => {
              if (onCta) onCta();
              else
                ui.submitFromPrompt({
                  precedingAssistantMessage: message,
                  userText: 'Tell me more.',
                });
            },
            onDecline: () => {
              // First-class decline (O1) — the outcome PATCH (Personalize
              // slice 8) will report this; until then the session budget
              // + dismissal stickiness carry the etiquette.
              console.info('[showpilot] offer declined');
            },
            onCollapse: () => {
              ui.setOfferBadge(1);
              proactiveGreeting = message; // reachable via the conversation
            },
          },
        );
        return;
      }

      mountPilotPrompt(
        {
          key: decision.decisionId,
          agentName: remoteConfig?.agent.name ?? 'Pilot',
          avatarUrl: remoteConfig?.theme.avatarUrl ?? null,
          imageUrl,
          headline,
          message,
          chips,
          cta: ctaLabel ? { label: ctaLabel } : null,
          primaryColor: remoteConfig?.theme.primaryColor,
        },
        {
          onSubmit: (value) => {
            ui.submitFromPrompt({
              precedingAssistantMessage: message,
              userText: value,
            });
          },
          onCta,
          onDismiss: () => {
            // Slice 8 will fire an outcome PATCH (dismissed) here.
          },
        },
        {
          // M3 (behind ui.mobileV2) — the unified Card/teaser: device-aware
          // rendering (phone = full-width teaser), P9 pacing budget, P7
          // greeter-video teaser dock.
          mobileV2: ui.isMobileV2(),
          teaserDock: ui.isMobileV2() ? ui.avatarTeaserDock : undefined,
          greeterFocalPoint: remoteConfig?.voice.greeterFocalPoint,
        },
      );
    };
    firePostDecide();
    // Moments → decide (Phase C): dwell / scroll / exit, each once per
    // page, named on the request. Skipped while the visitor is already in
    // conversation or watching content — those are not quiet moments.
    // The visit log runs for the whole tab, in or out of conversation —
    // it is what `# This visit` renders from (slice 2, 2026-10-28).
    installVisitLog();
    const moments = installMomentWatchers((m) => {
      const snap = ui.getWidgetStateSnapshot();
      if (snap.state === 'conversation' || snap.experienceActive) return;
      firePostDecide(m);
    });
    // SPA nav re-trigger — when the host page client-routes to a new
    // URL, fire another /v1/decide so the engage greeter (or future
    // surfaces) can re-personalize for the new page context. Throttle
    // is built in by the lastHref guard inside installSpaNavWatcher.
    window.addEventListener('showpilot:spa-nav', () => {
      moments.reset();
      firePostDecide();
    });
  }

  // Warm the welcome-greeting audio cache the moment we know the
  // welcomeMessage. /speak is idempotent + cached, so calling it here
  // means the user's first Talk Now click is just an audio fetch
  // (~50ms warm-cache HEAD + browser mp3 stream) instead of TTS
  // generation (~2s cold path). Failures are silent — the click
  // path will retry on demand.
  let warmedGreetingUrl: string | null = null;
  let warmingPromise: Promise<string | null> | null = null;
  if (voiceEnabled && welcomeMessage && !config.mock) {
    warmingPromise = speakText(config.apiBaseUrl, config.publicId, welcomeMessage)
      .then(({ audioUrl }) => {
        warmedGreetingUrl = audioUrl;
        // Tell the browser to start fetching the mp3 in the background
        // so playback on click is instant.
        const audio = new Audio(audioUrl);
        audio.preload = 'auto';
        audio.load();
        return audioUrl;
      })
      .catch((err) => {
        console.warn('[showpilot] /speak warm failed', err);
        return null;
      });
  }

  let sessionId = snapshot.sessionId;
  const history: ChatHistoryMessage[] = [...snapshot.history];

  // Slice 6.6 — the personalized greeting the proactive /v1/decide
  // call rendered into the engage card. Persisted here so the FIRST
  // /chat turn can hand it to the LLM as the prior assistant message;
  // without this, the chat-runtime starts with an empty history and
  // re-greets the visitor verbatim (the duplicate "Welcome back!"
  // bug). Cleared after the first turn pushes it into `history` so
  // we don't double-prepend on subsequent turns.
  let proactiveGreeting: string | null = null;

  // Eagerly create the session on widget mount so the first /chat call
  // doesn't pay the create round-trip. The server may also return
  // `firstJourneyStep` for chat-mode journeys, but we INTENTIONALLY
  // do NOT auto-dispatch it — the journey is not the cold open. It
  // fires when the visitor actually engages (sends first message or
  // clicks an option), at which point chat-pipeline emits the same
  // journey-step events through the normal SSE path.
  // Arrival context for session create (P2.11): the session row is
  // born HERE for most visits, so it must carry pageUrl/referrer/utm +
  // launch context (incl. the experience publicId for share-view
  // attribution) — the chat turn can't backfill an existing row.
  const sessionLaunchContext: Record<string, unknown> = {
    ...config.launchContext,
    ...(config.experience ? { experiencePublicId: config.experience.publicId } : {}),
  };
  const sessionArrival = (): Parameters<typeof createSession>[5] => ({
    context: { pageUrl: window.location.href, ...readPageContext() },
    // Page SDK context() set before the session exists rides the launch context.
    launchContext: { ...sessionLaunchContext, ...(getPageContext() ?? {}) },
  });

  if (!sessionId && !config.mock && earlyActions.length === 0) {
    try {
      const created = await createSession(
        config.apiBaseUrl,
        config.publicId,
        null,
        getOrCreateVisitorFingerprint(),
        undefined,
        sessionArrival(),
      );
      sessionId = created.sessionId;
    } catch (err) {
      // Non-fatal — sendHandler creates a session lazily on first message.
      console.warn('[showpilot] eager session create failed', err);
    }
  }

  /**
   * Text Relay v1 — open the SSE data plane unconditionally once we
   * have a session, so rep messages posted from the admin Live page
   * arrive in real time. The channel was originally voice-only; the
   * envelope and origin allowlist make it equally safe for text-mode
   * use. Voice tool side-effects when voice is engaged still come
   * through this same stream (no separate channel needed).
   *
   * The widget already wires `handleChatEvent` for every ChatEvent
   * type — including `rep-message` — so we just need to route incoming
   * events into the same dispatcher.
   */
  let chatEventSub: VoiceEventSubscription | null = null;
  if (sessionId && !config.mock) {
    try {
      chatEventSub = subscribeVoiceEvents({
        chatApiBaseUrl: config.apiBaseUrl,
        sessionId,
        publicId: config.publicId,
        onEvent: (event) => {
          // Only out-of-band events (rep-message today; future
          // server-originated nudges) should land here for text-mode
          // sessions. Voice-mode events arrive on the same channel —
          // when voice is engaged the voice bootstrap opens its OWN
          // subscription, so we skip transcript events here to avoid
          // double-rendering.
          if (
            event.type === 'voice-transcript' ||
            event.type === 'voice-caption' ||
            event.type === 'voice-bot-dropped'
          )
            return;
          if (event.type === 'rep-message') {
            ui.handleChatEvent(event);
            history.push({
              role: 'assistant',
              content: event.payload.text,
            });
            persist({
              publicId: config.publicId,
              sessionId,
              history,
            });
            return;
          }
          // Slice E — rep lifecycle + typing indicator. None of these
          // belong in `history` (the LLM doesn't need to see them on
          // the next turn). Just hand to the UI dispatcher.
          if (
            event.type === 'rep-joined' ||
            event.type === 'rep-released' ||
            event.type === 'rep-typing'
          ) {
            ui.handleChatEvent(event);
            return;
          }
          // Rep-initiated proactive invite ("Relay request"). Fires even
          // with chat closed — the dispatcher pops a one-time toaster.
          // Not part of `history`; the LLM doesn't need to see it.
          if (event.type === 'rep-invite') {
            ui.handleChatEvent(event);
            return;
          }
          // Slice F — Daily call lifecycle. Widget owns the overlay
          // mount + accept/decline card; we just route the events.
          if (
            event.type === 'call-invite' ||
            event.type === 'call-ended' ||
            event.type === 'call-declined'
          ) {
            ui.handleChatEvent(event);
            return;
          }
          // Rep/booking slice 3 — a rep firing "Send times" from Relay
          // or the callback-SLA fallback pushes the booking card
          // out-of-band. ONLY those (they mark askChoice:false): agent
          // and voice turns deliver meeting-slots on their own channels,
          // and routing them here too rendered the card twice (dogfood
          // 2026-07-26). Not part of `history`.
          if (event.type === 'meeting-slots') {
            if (event.payload.askChoice === false) ui.handleChatEvent(event);
            return;
          }
        },
      });
    } catch (err) {
      console.warn('[showpilot] rep-message subscription failed', err);
    }
  }
  // Best-effort cleanup on page unload — the connection drops anyway
  // when the tab navigates, but explicit close avoids the EventSource
  // backoff retry firing during unload.
  if (chatEventSub) {
    window.addEventListener('beforeunload', () => {
      chatEventSub?.close();
    });
  }

  /**
   * Wire option-click answers (yes/no / single / multi / action) to
   * the chat-api journey-answer endpoint. Free-text answers come
   * through `onSend` as normal chat messages and are captured by
   * chat-pipeline's journey branch.
   */
  ui.onJourneyAnswer(async (stepId, value) => {
    if (!sessionId || config.mock) return;
    try {
      const result = await submitJourneyAnswer(config.apiBaseUrl, config.publicId, {
        sessionId,
        stepId,
        value,
      });
      if (result.next) {
        ui.handleChatEvent({ type: 'journey-step', payload: result.next });
      } else if (result.completed) {
        ui.handleChatEvent({
          type: 'journey-complete',
          payload: { answeredCount: result.answeredCount ?? 0 },
        });
      }
    } catch (err) {
      ui.showError(err instanceof Error ? err.message : 'Couldn’t save your answer.');
    }
  });

  // Contact-capture POST — /v1/contact-capture funnels each non-empty
  // field into captureQualifyingAnswer + identifyByEmail (HubSpot
  // lookup + create-on-miss). Server enforces origin + visitor
  // fingerprint; we just pass them through. Shared by the form card
  // submit AND the offer interstitial's email (QW-2).
  const postContactCapture = async (
    fields: Record<string, string>,
    intent?: 'contact' | 'callback' | 'booking',
    formId?: string,
    answers?: Record<string, string>,
  ): Promise<void> => {
    if (config.mock) return; // dev preview — no server side-effect
    const res = await fetch(`${config.apiBaseUrl}/v1/contact-capture`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        publicId: config.publicId,
        fingerprint: getOrCreateVisitorFingerprint(),
        sessionId: sessionId ?? undefined,
        fields,
        // Capture ladder (slice 3) — 'callback' pages a rep now.
        ...(intent ? { intent } : {}),
        // F-3 — completion runs the form's after-they-finish ladder.
        ...(formId ? { formId } : {}),
        // F-4 — non-quartet answers (declared-keys-only server-side).
        ...(answers && Object.keys(answers).length > 0 ? { answers } : {}),
      }),
    });
    if (!res.ok) {
      let msg = "Couldn't save that — try again?";
      try {
        const body = (await res.json()) as { error?: { message?: string } };
        if (body.error?.message) msg = body.error.message;
      } catch {
        /* swallow */
      }
      throw new Error(msg);
    }
    // GTM bridge: confirmed contact capture. Deliberately NO field
    // values in the payload — never push PII into the dataLayer.
    pushMilestone('lead_captured', {});
    // F-3 — redirect-mode confirmation: the form's after config sends
    // the visitor to a page once the submit lands.
    try {
      const body = (await res.json()) as { redirectUrl?: string };
      if (body.redirectUrl) {
        ui.handleChatEvent({ type: 'navigate-url', payload: { url: body.redirectUrl } });
      }
    } catch {
      /* no body — fine */
    }
  };
  contactCapturePost = postContactCapture;
  ui.onContactCapture(postContactCapture);

  // Slice F — Daily call lifecycle. Widget owns the overlay mount;
  // we POST the matching chat-api endpoint to stamp the audit row +
  // notify the rep's admin panel. Decline → /decline. End → /end.
  // These are unauthenticated public POSTs from the widget — the
  // server validates via sessionId existence + a public-id check,
  // not the internal-key path the admin uses.
  ui.onCallLifecycle((event) => {
    if (config.mock || !sessionId) return;
    const path = event.kind === 'declined' ? 'decline' : 'end';
    // Fire-and-forget — the local UI has already cleaned up; failure
    // to stamp just means the admin sees a stale "in-call" state for
    // a few seconds until the Daily room expires.
    void fetch(
      `${config.apiBaseUrl}/v1/sessions/${encodeURIComponent(sessionId)}/call/${encodeURIComponent(event.callId)}/${path}`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicId: config.publicId }),
      },
    ).catch(() => {});
  });

  ui.onTalkNow(async (): Promise<string | null> => {
    if (warmedGreetingUrl) return warmedGreetingUrl;
    if (warmingPromise) return warmingPromise;
    // Fallback path: cold + uncached. Should rarely hit since the
    // warming kicks off at config-load.
    const text = welcomeMessage.trim();
    if (!text || config.mock) return null;
    try {
      const { audioUrl } = await speakText(config.apiBaseUrl, config.publicId, text);
      warmedGreetingUrl = audioUrl;
      return audioUrl;
    } catch (err) {
      console.warn('[showpilot] /speak failed', err);
      return null;
    }
  });

  // Voice session — owned by index.ts because it spans across the
  // voice-toggle UI hook and cross-page session continuity. The Daily
  // migration (Phase 1) makes voice & text orthogonal: typed input
  // always goes through /chat, voice goes through Daily WebRTC.
  let voiceHandle: VoiceBootstrapHandle | null = null;
  // Wall-clock when the previous user fragment landed — used to merge
  // Deepgram's multi-frame finals into one chat bubble per utterance.
  let lastUserFragmentAt = 0;

  // Mount the Preact voice-status badge in the header anchor. The
  // component renders only when voiceState.name !== 'idle', so it
  // takes no layout space when not in voice mode.
  mountPreactIsland(ui.voiceBadgeAnchor, <VoiceStatusBadge />);

  // Dev-only: `?sp_summon` forces the connecting state so the summon/load
  // overlay can be eyeballed without standing up the voice stack. Inert in
  // production — only fires with the explicit query param.
  if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('sp_summon')) {
    setTimeout(() => voiceTransition.toConnecting('preview', 'preview'), 500);
  }

  // Dev-only: `?sp_live` fakes a live avatar stream so the live-call layout
  // and the control pill (mic · minimize · End) can be eyeballed without the
  // voice stack. hasLive flips true, the pill renders over the "face".
  // Inert in production (explicit param only).
  if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('sp_live')) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    let painted = false;
    // Paint a brand-tinted fill first so the pill is always visible even
    // before (or without) the poster — keeps the preview robust if R2 sends
    // no CORS headers (the cross-origin image would taint the canvas).
    const tick = () => {
      if (ctx && !painted) {
        const g = ctx.createLinearGradient(0, 0, 0, 512);
        g.addColorStop(0, '#2b3a67');
        g.addColorStop(1, '#0b1733');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, 512, 512);
      }
      requestAnimationFrame(tick);
    };
    tick();
    // Capture immediately — the pill shows regardless of the poster.
    const stream = (
      canvas as HTMLCanvasElement & { captureStream(fps?: number): MediaStream }
    ).captureStream(30);
    botVideoStream.value = stream;
    // Best-effort: paint Alex's poster only if it loads CORS-clean (drawing
    // a tainted image would break the captured track).
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      painted = true;
      const draw = () => {
        if (ctx) ctx.drawImage(img, 0, 0, 512, 512);
        if (painted) requestAnimationFrame(draw);
      };
      draw();
    };
    img.src = 'https://pub-b83d2cb8bc8342bf98eddd853dc4acad.r2.dev/system/personas/alex/poster.jpg';
    // Cycle the live state so the identity chip + glow animate in the preview:
    // ~3s listening, ~3s speaking (with a breathing bot level for the glow).
    let speaking = false;
    const flip = () => {
      speaking = !speaking;
      voiceState.value = {
        ...voiceState.value,
        name: speaking ? 'speaking' : 'listening',
        micAudioLevel: speaking ? 0 : 0.4,
      };
    };
    flip();
    setInterval(flip, 3200);
    const level = () => {
      if (speaking) {
        const v = 0.35 + 0.35 * Math.abs(Math.sin(performance.now() / 320));
        voiceState.value = { ...voiceState.value, botAudioLevel: v };
      }
      requestAnimationFrame(level);
    };
    requestAnimationFrame(level);
  }

  // Presence-law kickoff state (see ui.onContentOpened): one hidden
  // "you lead it now" nudge per tour, flushed between turns.
  let turnInFlight = false;
  const turnIdleWaiters: Array<() => void> = [];
  let pendingTourLead: string | null = null;
  const tourLeadNudged = new Set<string>();
  const flushTourLead = (): void => {
    if (!pendingTourLead || turnInFlight) return;
    if (voiceState.value.name !== 'idle') {
      pendingTourLead = null;
      return;
    }
    const text = pendingTourLead;
    pendingTourLead = null;
    void sendChatMessage(text, { fromVoice: false, hidden: true });
  };

  const sendChatMessage = async (
    message: string,
    opts: { fromVoice: boolean; hidden?: boolean },
  ): Promise<void> => {
    // If the engage greeter already chirped a personalized welcome,
    // seed the chat history with it as the assistant's prior turn so
    // the LLM doesn't open the conversation by repeating the same
    // greeting. Only do this on the first turn of an unprimed
    // history — subsequent turns already see the full transcript.
    if (proactiveGreeting && history.length === 0) {
      history.push({ role: 'assistant', content: proactiveGreeting });
      proactiveGreeting = null;
    }
    // Experience-mode seed: the goal drives the agent but was never typed
    // by the visitor, so it goes into history (the model reads it) without
    // an on-screen "visitor said" bubble. The `hidden` flag persists WITH
    // the entry so renderHistory skips it on resume too — without it,
    // replayed sessions painted seeds and continuation ticks as visitor
    // bubbles (David's screenshot, 2026-08-23).
    if (!opts.hidden) ui.appendUserMessage(message);
    history.push({ role: 'user', content: message, ...(opts.hidden ? { hidden: true } : {}) });

    // ONE VOICE: during a live call the bot answers typed text aloud and
    // persists it; a /chat turn here ran a second brain on the same
    // message (text reply + spoken reply, live 2026-09-08). Hidden seeds
    // keep their own voice-idle guards and never take this path.
    if (
      routeTypedMessage({ hidden: opts.hidden, mock: config.mock }) === 'voice' &&
      notifyVoiceOfVisitorText(message)
    ) {
      return;
    }

    turnInFlight = true;
    ui.setStreaming(true);
    try {
      if (!sessionId && !config.mock) {
        const s = await createSession(
          config.apiBaseUrl,
          config.publicId,
          null,
          getOrCreateVisitorFingerprint(),
          undefined,
          sessionArrival(),
        );
        sessionId = s.sessionId;
      }
      if (!sessionId) sessionId = `mock_${Date.now()}`;

      let assistantBuffer = '';
      const onEvent = (event: ChatEvent): void => {
        if (event.type === 'message') assistantBuffer += event.payload.text;
        if (event.type === 'done' && assistantBuffer) {
          // Store the finished reply NOW. The server sends a navigate-url
          // AFTER `done` (the words first, then the move); the page
          // unloads before the post-stream persist below would run.
          const replyChars = assistantBuffer.length;
          history.push({ role: 'assistant', content: assistantBuffer });
          assistantBuffer = '';
          persist({ publicId: config.publicId, sessionId, history });
          // The reply is on screen: the quiet clock starts (quiet-nudge.ts).
          quietNudge.onTurnDone(replyChars);
        }
        ui.handleChatEvent(event);
      };

      if (config.mock) {
        await mockStream({ sessionId, message, onEvent });
      } else {
        const pageElements = scrapePageElements();
        const widgetSnapshot = ui.getWidgetStateSnapshot();
        const fingerprint = getOrCreateVisitorFingerprint();
        // One retry when the stream dies before ANY event (a deploy's
        // connection reset, a router 502) — see chat-retry.ts. `retry`
        // tells the server not to store the visitor's message twice.
        let attempt = 0;
        let eventsSeen = 0;
        const countedOnEvent = (event: ChatEvent): void => {
          eventsSeen += 1;
          onEvent(event);
        };
        for (;;) {
          try {
            await streamChat({
              apiBaseUrl: config.apiBaseUrl,
              publicId: config.publicId,
              agentPreviewToken: config.agentPreviewToken,
              body: {
                sessionId,
                message,
                history,
                speak: opts.fromVoice,
                ...(attempt > 0 ? { retry: true } : {}),
                // Stage directions are stored as author_kind 'widget' so the
                // operator inbox never shows them as the visitor's words.
                ...(opts.hidden ? { hidden: true } : {}),
                context: {
                  pageUrl: window.location.href,
                  // Page SDK context(): what the host page says the agent should know now.
                  ...(getPageContext() ? { page: getPageContext()! } : {}),
                  // Send a weekday-bearing local string, NOT toISOString — the
                  // model can't reliably derive day-of-week from a bare ISO date
                  // (it read "2026-06-08" as Sunday when it's Monday). This is the
                  // visitor's own timezone. Stays within the contract's 64-char cap.
                  dateTime: formatLocalDateTime(),
                  ...(pageElements.length > 0 ? { pageElements } : {}),
                  widgetView: widgetSnapshot.view,
                  widgetState: widgetSnapshot.state,
                  experienceActive: widgetSnapshot.experienceActive,
                  // Directed Experience: the seed goal flips the agent into a
                  // directed (welcome + lead) stance for this shared session.
                  // NOT on presenter mounts — the PRESENTING stance owns the
                  // turn there, and the directed machinery would open library
                  // content over the demo stage (observed 2026-08-20 smoke).
                  ...(config.experience?.seed?.goal && !config.projectPublicId
                    ? { experienceGoal: config.experience.seed.goal }
                    : {}),
                  // Presenter — the hub whose demo stage this page is; the
                  // server flips the PRESENTING stance + stage tools on it.
                  ...(config.projectPublicId ? { projectPublicId: config.projectPublicId } : {}),
                  // Single-tour door — the /t/ link the viewer arrived through.
                  // PRESENCE LAW (site mounts): no hub key, but a tour is on
                  // the widget's own stage — stamp it alone; the server
                  // derives the parent hub and the hat arrives mid-turn,
                  // dropping again the moment the pane closes.
                  ...(config.projectPublicId
                    ? config.presenterTourPublicId
                      ? { tourPublicId: config.presenterTourPublicId }
                      : {}
                    : (() => {
                        const staged = ui.activeTourPublicId();
                        return staged ? { tourPublicId: staged } : {};
                      })()),
                  // v3 — an accepted open_experience offer rides the first
                  // message so the runtime launches it deterministically.
                  // One-shot: consumed at build time.
                  ...(pendingExperienceLaunch
                    ? {
                        launchExperienceId: pendingExperienceLaunch.experienceId,
                        launchOfferId: pendingExperienceLaunch.offerId,
                      }
                    : pendingOfferAccept
                      ? // Accepted non-experience offer — launchOfferId ALONE
                        // grounds the first turn (offer copy + deterministic
                        // open_content). One-shot, same as the launch above.
                        { launchOfferId: pendingOfferAccept.offerId }
                      : {}),
                  fingerprint,
                  // Current-touch signals: server persists these on session
                  // create (and ignores them on subsequent turns — see
                  // `chat-pipeline.ts:ensureSession`). Driving the
                  // "# Current Touch" prompt block — arrival-based
                  // personalization (greet the touch, not the person).
                  ...readPageContext(),
                  // This visit — landing, referrer, pages with dwell + scroll
                  // (visit-log.ts). The `# This visit` block on the server.
                  ...(() => {
                    const visit = visitSnapshot();
                    return visit ? { visit } : {};
                  })(),
                },
                // B1c: deployment context from embed attrs (+ the experience
                // publicId for share-view attribution). Server only persists
                // on session create; subsequent turns are no-ops, so sending
                // it every time is safe and keeps the widget stateless.
                ...(Object.keys(sessionLaunchContext).length > 0
                  ? { launchContext: sessionLaunchContext }
                  : {}),
              },
              onEvent: countedOnEvent,
            });
            break;
          } catch (err) {
            if (!shouldRetryTurn({ attempt, eventsSeen, err })) throw err;
            attempt += 1;
            console.warn('[showpilot] chat stream failed before any reply — retrying once', err);
            await new Promise<void>((r) => setTimeout(r, CHAT_RETRY_DELAY_MS));
          }
        }
      }

      // The launch/accept rode this message — never re-send it.
      pendingExperienceLaunch = null;
      pendingOfferAccept = null;
      if (assistantBuffer) {
        history.push({ role: 'assistant', content: assistantBuffer });
      }
      persist({ publicId: config.publicId, sessionId, history });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'request failed';
      ui.showError(`Sorry — ${message}`);
    } finally {
      turnInFlight = false;
      ui.setStreaming(false);
      // An Action Link clicked mid-reply waited for this `done`.
      for (const w of turnIdleWaiters.splice(0)) w();
      // A tour opened mid-turn queues its lead nudge — fire it now that
      // the turn is closed (one hidden turn; the hat rides its context).
      flushTourLead();
    }
  };

  // ACTION LINKS (Phase D): the runner lives in the lazy actions.js chunk;
  // it is created on the first fire and owns the create-vs-post decision,
  // the mid-reply wait and the hidden stage direction (see action-runner.ts).
  let actionRunner: Promise<ActionRunner> | null = null;
  // True while the current Action opened as a bar notification (phone tier 1/2).
  let actionBarTier = false;
  const getActionRunner = (): Promise<ActionRunner> => {
    if (!actionRunner) {
      actionRunner = loadActions()
        .then((m) =>
          m.createActionRunner({
            getSessionId: () => sessionId,
            createSessionWithAction: async (id, source) => {
              if (config.mock) {
                sessionId = `mock_${Date.now()}`;
                return { sessionId, targetKind: 'conversation' };
              }
              const arrival = sessionArrival() ?? {};
              const s = await createSession(
                config.apiBaseUrl,
                config.publicId,
                null,
                getOrCreateVisitorFingerprint(),
                undefined,
                {
                  ...arrival,
                  launchContext: { ...(arrival.launchContext ?? {}), action: id, actionSource: source },
                },
              );
              sessionId = s.sessionId;
              return { sessionId, targetKind: s.action?.targetKind ?? null };
            },
            postFire: (sid, id, source) =>
              config.mock
                ? Promise.resolve({ accepted: true, targetKind: 'conversation' })
                : postActionFire(config.apiBaseUrl, {
                    publicId: config.publicId,
                    sessionId: sid,
                    fingerprint: getOrCreateVisitorFingerprint(),
                    action: id,
                    source,
                  }),
            isPhone: () => deviceSig.value === 'phone',
            // The three tiers (action-tier.ts, decided in the lazy chunk):
            // a bar notification, the conversation sheet, or the desktop panel.
            openPanel: (open) => {
              actionBarTier = open.tier === 'bar';
              ui.setLayout(open.layout);
              // The sheet opens on the task: in conversation, no greeter stage.
              if (open.tier === 'sheet') ui.markConversationStarted();
            },
            waitTurnIdle: () =>
              turnInFlight ? new Promise<void>((r) => turnIdleWaiters.push(r)) : Promise.resolve(),
            sendStageDirection: async (text) => {
              await sendChatMessage(text, { fromVoice: false, hidden: true });
              // Bar tier: the agent's first move becomes the line above the bar.
              if (!actionBarTier) return;
              const last = history[history.length - 1];
              if (last?.role === 'assistant') ui.showBarNotice(stripInlineMarkup(last.content));
            },
            voiceLive: () => voiceState.value.name !== 'idle',
          }),
        )
        .catch((err: unknown) => {
          actionRunner = null;
          throw err;
        });
    }
    return actionRunner;
  };
  onAction = (id, source) => {
    actionEngaged = true;
    // The task is the opening: no "how can I help?" ahead of it.
    if (history.length === 0) ui.clearWelcome();
    void getActionRunner()
      .then((r) => r.request(id, source))
      .catch((err: unknown) => {
        // The chunk failed to load — the click still meant "talk to the agent".
        console.warn('[showpilot] action link runner failed to load', err);
        openConversation();
      });
  };
  for (const [id, source] of earlyActions.splice(0)) onAction(id, source);

  // MEDIA FLOOR (one-voice law): narrated media is a second speaker —
  // in a live call the bot speaking pauses it and standing down resumes
  // it (media-floor watches the voice state machine itself); in text
  // mode a clip ENDING is the agent's cue to retake the thread — one
  // hidden nudge, once per clip, only inside an existing conversation.
  const endNudged = new Set<string>();
  const stageMediaFloor = initMediaFloor({
    onEnded: (contentId, title) => {
      if (!sessionId || config.mock) return;
      if (endNudged.has(contentId)) return;
      endNudged.add(contentId);
      // Live call → Daily app message; the bot injects the note and
      // speaks proactively (same pipe as page-changed). No call → the
      // hidden text nudge.
      if (notifyVoiceOfMediaEnded(title)) return;
      if (voiceState.value.name !== 'idle') return; // call up but pipe down — stay quiet
      void sendChatMessage(
        '(the video just ended — the viewer is back. One line on what it showed, then the natural next step.)',
        { fromVoice: false, hidden: true },
      );
    },
  });

  // Presenter (agent-led demos): route the five stage events + own the
  // continuation tick. On EVERY mount now (presence law — a site agent
  // gains the hat when a tour is on its stage, and its presentTour /
  // tourControl events need the router); the continuation tick stays
  // presenter-mount-only — mid-conversation site chats pace themselves,
  // a hidden nudge there would talk over the visitor's own thread.
  // STAGE TRUTH (screen-truth loop): ack where the tour on the stage
  // ACTUALLY is after every applied move — the server grounds tourControl
  // results and next-turn prompts on these, so the brain narrates the
  // screen the viewer really sees (including their own clicks, which no
  // server-side prediction can know about). Viewer-driven moves during a
  // live call also drop a silent context note to the bot.
  // A tour's FIRST ack is the tour APPEARING (screen 1 on mount), not a
  // move — voice content_view opens don't route through presenter-stage,
  // so noteAgentMove never fires and the open classified as 'viewer'.
  // Found live 2026-08-25: the bot got "the viewer moved the tour
  // THEMSELVES — do not take the wheel back" seconds after content_view
  // told it "YOU present it". The opening ack still lands in stage
  // memory; only the viewer-moved voice note is suppressed for it.
  const ackedTours = new Set<string>();
  // PLAYOUT LOG (rails 1.1/1.2): mirror each voice playout-done ack into
  // the session event log while a tour is on stage, so pacing replays
  // next to presents / controls / screen acks. Gated exactly like the
  // rest of the presenter plane: a presenter mount or an open tour.
  setPlayoutLogListener(() => {
    if (!sessionId || config.mock) return;
    const staged = ui.activeTourPublicId();
    if (!staged && !config.projectPublicId) return;
    postSessionEvent(config.apiBaseUrl, sessionId, {
      type: 'presenter-playout',
      payload: staged ? { tourPublicId: staged } : {},
    });
  });
  // STAGE TRUTH — CLOSE (2026-08-28): the tour left the content pane by
  // ANY path (viewer's X, backdrop, agent close/navigate). Ack it so
  // stage memory empties and the agent stops narrating a stage nobody
  // sees — and so a later reopen is a real re-present, not a "continue".
  ui.onTourStageClosed((tourPublicId) => {
    if (!sessionId || config.mock) return;
    postSessionEvent(config.apiBaseUrl, sessionId, {
      type: 'presenter-screen',
      payload: { tourPublicId, closed: true, source: 'viewer' },
    });
  });
  const stageTruth = createStageTruth({
    post: (ack) => {
      if (!sessionId || config.mock) return;
      postSessionEvent(config.apiBaseUrl, sessionId, {
        type: 'presenter-screen',
        payload: { ...ack },
      });
      const firstSight = !ackedTours.has(ack.tourPublicId);
      ackedTours.add(ack.tourPublicId);
      if (!firstSight && ack.source === 'viewer')
        notifyVoiceOfScreenChange({ screen: ack.screen, total: ack.total });
    },
  });
  // TOUR-END OUTCOME (slice 4): the player's `completed` event — end of
  // the story OR its operator-marked completion step — is the agent's
  // cue to LAND it (outcome + CTA), the media-ended analog for tours.
  // Relevance gate: presenter mounts and the content pane's tour are
  // the agent's business; a live call always is (the bot presents what
  // it opened). A bare self-play launcher stays silent.
  const tourEndRetake = createTourEndRetake({
    isRelevant: (id) =>
      !!config.projectPublicId ||
      ui.activeTourPublicId() === id ||
      voiceState.value.name !== 'idle',
    record: (id) => {
      if (!sessionId || config.mock) return;
      // The session-plane completion record — the demo-watched rep
      // alert (server) and the inbox Content tab read this; the tours
      // ingest plane keeps its own telemetry independently.
      postSessionEvent(config.apiBaseUrl, sessionId, {
        type: 'content-completed',
        payload: { contentId: id, contentType: 'tour' },
      });
    },
    notifyVoice: () => notifyVoiceOfTourEnded(),
    // The agent's own next/goTo to the last screen lands from its
    // tourControl result; a second cue here made it close twice.
    agentDrove: (id) => stageTruth.agentLandedFinal(id),
    sendNudge: () => {
      if (!sessionId || config.mock) return;
      if (voiceState.value.name !== 'idle') return; // call up but pipe down — stay quiet
      void sendChatMessage(TOUR_END_NUDGE, { fromVoice: false, hidden: true });
    },
  });

  const presenterStage = createPresenterStage({
    openTour: (id) => ui.openPresenterTour(id),
    closeStage: () => ui.closePresenterStage(),
    getControl: () => window.__spTourControl,
    isMediaHolding: () => stageMediaFloor.isHolding(),
    onAgentMove: () => stageTruth.noteAgentMove(),
    sendTick: config.projectPublicId
      ? (text) => {
          void sendChatMessage(text, { fromVoice: false, hidden: true });
        }
      : () => {},
  });
  ui.setPresenter(presenterStage);

  // QUIET NUDGE (2026-10-28) — the chat thread's silence follow-up, parity
  // with the voice bot's: after the agent's reply, if the visitor says
  // nothing for a reading-dwell, one hidden turn lets the agent carry the
  // conversation forward (or stayQuiet). Never on presenter mounts — the
  // stage tick above owns that pacing — and never while a call, media,
  // content, a draft, or a hidden tab means "busy, not quiet".
  const quietNudge = createQuietNudge({
    sendTick: (text) => {
      void sendChatMessage(text, { fromVoice: false, hidden: true });
    },
    isBusy: () => {
      if (config.projectPublicId) return true;
      if (voiceState.value.name !== 'idle') return true;
      if (stageMediaFloor.isHolding()) return true;
      if (ui.hasDraft() || ui.hasContentOpen()) return true;
      const snap = ui.getWidgetStateSnapshot();
      if (snap.state !== 'conversation' || snap.experienceActive) return true;
      return typeof document !== 'undefined' && document.visibilityState === 'hidden';
    },
  });

  const onTourBridgeEvent = (raw: unknown) => {
    const m = raw as {
      source?: string;
      type?: string;
      event?: string;
      publicId?: string | null;
      screen?: number;
      totalScreens?: number;
      text?: string;
    } | null;
    if (!m || m.source !== 'showpilot' || m.type !== 'sp:tour:event') return;
    if (typeof m.publicId !== 'string') return;
    if (m.event === 'completed') {
      // The story is told: the stage stops ticking until the viewer
      // speaks or a new present, and the retake decides whether to cue.
      presenterStage.noteTourCompleted();
      tourEndRetake.onCompleted(m.publicId);
      return;
    }
    if (m.event !== 'step' || typeof m.screen !== 'number') return;
    stageTruth.onStep(
      m.publicId,
      m.screen,
      m.totalScreens,
      typeof m.text === 'string' ? m.text : undefined,
    );
  };
  // Same-window players (content pane, /p/ stage) dispatch a CustomEvent;
  // the /t/?embed=1 iframe fallback posts to the parent instead.
  window.addEventListener('sp:tour:event', (e) => onTourBridgeEvent((e as CustomEvent).detail));
  window.addEventListener('message', (e) => onTourBridgeEvent((e as MessageEvent).data));

  // Presenter: the auto-seed is HELD, not instant — a viewer heading for
  // Talk Now must not find the text presenter already mid-demo (David,
  // 2026-08-20 live test: the demo started before the bot joined the
  // room). The stage still shows the first tour immediately (static,
  // agent mode); only the NARRATION waits. Cancelled by voice starting
  // or the viewer engaging first.
  let cancelPresenterSeed: (() => void) | null = null;

  ui.onSend((message, opts) => {
    // A REAL viewer turn (typed or chip) resets the presenter tick budget
    // and takes over from the pending auto-seed.
    cancelPresenterSeed?.();
    presenterStage?.noteViewerInput();
    // A real visitor turn (typed or chip): the quiet slate is clean; a
    // hidden direction is not the visitor speaking.
    if (!opts.hidden) quietNudge.noteVisitorInput();
    void sendChatMessage(message, opts);
  });

  // Experience mode (Guided Experiences P2): auto-start the seeded guided
  // journey. The seed goal drives the agent's first turn (→ assembleJourney
  // → openContent) as a HIDDEN user message — the visitor never typed it,
  // so no "visitor said" bubble renders; they just land and the agent
  // begins guiding. Fires once, only when history is empty (a resumed
  // session already has its opening). Small delay lets the FULL viewer
  // paint first.
  // Presenter mounts: the playlist IS the hub's tours — static, from the
  // shell. Load it on EVERY visit (a resumed session skips the seed block
  // below, which would leave the stage dark and the playlist empty).
  if (config.experience && config.projectPublicId && history.length > 0 && !config.mock) {
    if (config.experience.playlist.length > 0) {
      ui.loadDurablePlaylist(config.experience.playlist);
    }
  }
  if (config.experience && history.length === 0 && !config.mock) {
    const exp = config.experience;
    const startSeed = (): void => {
      // The agent welcomes + narrates alongside (streams). assembleJourney
      // stays for dynamic updates on interaction.
      if (exp.seed.goal) {
        const seedGoal = exp.seed.goal;
        setTimeout(() => {
          void sendChatMessage(seedGoal, { fromVoice: false, hidden: true });
        }, 250);
      }
    };

    if (exp.gate === 'none') {
      // Public / passport already satisfied — durable first touch: render
      // the curated playlist + open the first item instantly, agent leads.
      if (exp.playlist.length > 0) ui.loadDurablePlaylist(exp.playlist);
      if (config.projectPublicId && exp.seed.goal) {
        // Presenter entry model: 'live' never seeds (the Join already
        // happened; voice auto-starts and the BOT opens the demo).
        // 'chat' seeds right away — the demo is text-led by design.
        // Legacy shells (no delivery) keep the cautious 6s hold so a
        // voice-first viewer isn't raced by the text presenter.
        if (config.presenterDelivery !== 'live') {
          const seedGoal = exp.seed.goal;
          const t = setTimeout(
            () => {
              cancelPresenterSeed = null;
              void sendChatMessage(seedGoal, { fromVoice: false, hidden: true });
            },
            config.presenterDelivery === 'chat' ? 350 : 6000,
          );
          cancelPresenterSeed = () => {
            clearTimeout(t);
            cancelPresenterSeed = null;
          };
        }
      } else {
        startSeed();
      }
    } else {
      // Gated / reverse — resolve the gate against THIS visitor's passport
      // (the fingerprint isn't known server-side). A returning captured
      // visitor unlocks with no form; otherwise show the gate.
      const fp = getOrCreateVisitorFingerprint();
      const applyUnlock = (r: {
        gate: 'none' | 'capture' | 'reverse';
        playlist: typeof exp.playlist;
        totalCount: number;
        /** G-1 — content_gate form fields + id (absent on the fallback
         *  path when /unlock itself failed). */
        formFields?: unknown;
        formId?: string | null;
      }): void => {
        if (r.gate === 'none') {
          if (r.playlist.length > 0) ui.loadDurablePlaylist(r.playlist);
          startSeed();
          return;
        }
        // reverse: show the preview behind the gate; capture: blank behind.
        if (r.gate === 'reverse' && r.playlist.length > 0) {
          ui.loadDurablePlaylist(r.playlist);
        }
        ui.mountExperienceGate({
          fields: exp.fields,
          mode: r.gate,
          lockedCount: Math.max(0, r.totalCount - r.playlist.length),
          formFields: r.formFields,
          formId: r.formId ?? null,
          onCaptured: () => {
            // Now the visitor has a passport — re-unlock for the full set.
            void unlockExperience(config.apiBaseUrl, exp.publicId, fp)
              .then((full) => {
                if (full.playlist.length > 0) ui.loadDurablePlaylist(full.playlist);
                startSeed();
              })
              .catch((err) => console.warn('[showpilot] re-unlock failed', err));
          },
        });
      };
      void unlockExperience(config.apiBaseUrl, exp.publicId, fp)
        .then(applyUnlock)
        .catch((err) => {
          console.warn('[showpilot] unlock failed', err);
          // Fail closed to the inlined (non-passport) view + gate.
          applyUnlock({
            gate: exp.gate,
            playlist: exp.playlist,
            totalCount: exp.totalCount,
            formFields: exp.formFields,
            formId: exp.formId ?? null,
          });
        });
    }
  }

  // Persist content opens to session_events so the admin Conversations
  // inbox Content tab can show them. Covers paths the LLM tool dispatch
  // misses: agent-link chip clicks, recommendation card clicks,
  // playlist drawer clicks. Fire-and-forget; never blocks UI.
  ui.onContentOpened((it) => {
    if (!sessionId || config.mock) return;
    postSessionEvent(config.apiBaseUrl, sessionId, {
      type: 'content-opened',
      payload: {
        contentId: it.content_id,
        contentType: it.content_type ?? 'content',
        title: it.name ?? '',
        url: it.url ?? null,
        thumbnailUrl: it.image ?? null,
      },
    });
    // PRESENCE LAW kickoff (text mode): a tour landing on a SITE
    // mount's stage needs the agent to take the lead — but the turn
    // that opened it is already streaming and the hat (presenter tools)
    // only arrives with the NEXT turn's context. Queue one hidden nudge
    // per tour; it fires when the current turn closes (or right away on
    // a viewer click with no turn in flight). Voice covers itself: the
    // bot's content_view result carries the lead instruction.
    if (
      !config.projectPublicId &&
      it.content_type === 'tour' &&
      voiceState.value.name === 'idle' &&
      !tourLeadNudged.has(it.content_id)
    ) {
      tourLeadNudged.add(it.content_id);
      pendingTourLead =
        '(a tour just opened on the stage — you LEAD it now. Frame what this story shows in one line, then present beat by beat: tourControl to advance, focusOn to point. Never tell them to click through.)';
      if (!turnInFlight) flushTourLead();
    }
  });

  // Consumption depth on agent-delivered assets — the differentiator cut.
  // Fired at milestones (video % watched) from the content viewer; the ≥90%
  // crossing also posts content-completed. Fire-and-forget.
  ui.onContentProgress((p) => {
    if (!sessionId || config.mock) return;
    postSessionEvent(config.apiBaseUrl, sessionId, {
      type: 'content-progress',
      payload: {
        contentId: p.contentId,
        contentType: p.contentType,
        kind: p.kind,
        percent: p.percent,
        ...(p.watchedSecs != null ? { watchedSecs: p.watchedSecs } : {}),
      },
    });
    if (p.done) {
      pushMilestone('content_completed', { title: p.title, type: p.contentType });
      postSessionEvent(config.apiBaseUrl, sessionId, {
        type: 'content-completed',
        payload: { contentId: p.contentId, contentType: p.contentType },
      });
    }
  });

  // v3-B — slot-chip booking. The server books AND records the
  // meeting-booked event itself (lead/conversion/Slack ride the same
  // pipe), so unlike onMeetingBooked below we only fire the client-side
  // milestone + agent acknowledgement here — never a second session event.
  ui.onBookSlot(async (body) => {
    if (!sessionId || config.mock) return { ok: false as const };
    const result = await postBookSlot(config.apiBaseUrl, sessionId, body);
    if (result.ok) {
      pushMilestone('meeting_booked', { provider: 'hubspot' });
      const sentToVoice = notifyVoiceOfVisitorAction({
        kind: 'book_meeting',
        label: 'a meeting',
      });
      if (!sentToVoice) {
        // A stage direction, not the visitor's words: never a visible bubble
        // (phone sweep 2026-10-20 saw it rendered as the visitor's own turn).
        void sendChatMessage('(I just booked the meeting.)', { fromVoice: false, hidden: true });
      }
    }
    return result;
  });

  // Slice-1 slot-ladder telemetry — the two rungs only the client can
  // observe (chip tap; ladder degrading to the modal). Fire-and-forget;
  // the server records the other rungs (slots-shown/conflict/failed).
  ui.onSlotFunnel((type, payload) => {
    if (!sessionId || config.mock) return;
    postSessionEvent(config.apiBaseUrl, sessionId, { type, payload });
  });

  // P-2 — post-email route-readiness (+ gap answers). Null on any
  // failure → the card keeps its pre-email routing.
  ui.onRouteReadiness(async (answers, force) => {
    if (!sessionId || config.mock) return null;
    return postRouteReadiness(config.apiBaseUrl, sessionId, {
      ...(answers ? { answers } : {}),
      ...(force ? { force: true } : {}),
    });
  });

  // Slice 3 — the booking picker hydrates its full availability window
  // (and pages "Later dates") through this feed.
  ui.onSlotOptions(async (fromIso) => {
    if (!sessionId || config.mock) return null;
    return fetchMeetingOptions(config.apiBaseUrl, sessionId, fromIso);
  });

  // Confirmed booking — the visitor completed scheduling in the in-widget
  // modal (provider postMessage, e.g. Calendly event_scheduled). This is the
  // meeting-booked GROUND TRUTH (opening the modal ≠ booked); it's the only
  // producer of the meeting-booked session event → Insights "Meetings".
  ui.onMeetingBooked((p) => {
    if (!sessionId || config.mock) return;
    pushMilestone('meeting_booked', { provider: p.provider });
    postSessionEvent(config.apiBaseUrl, sessionId, {
      type: 'meeting-booked',
      payload: { provider: p.provider, bookingUrl: p.bookingUrl },
    });
    // Tell the agent the visitor just confirmed the booking so it
    // acknowledges and keeps the conversation going instead of going
    // silent after the modal closes. Same dual-path pattern as agent-link
    // clicks: voice → app-message (interrupt + system note + force a turn);
    // text → a short synthetic user message that triggers the next turn.
    // The synthetic message renders as a brief user bubble + locks input
    // during the reply — accepted tradeoff so the agent reacts (see
    // project_visitor_action_notification).
    const sentToVoice = notifyVoiceOfVisitorAction({
      kind: 'book_meeting',
      label: 'a meeting',
    });
    if (!sentToVoice) {
      // A stage direction, not the visitor's words: never a visible bubble
      // (phone sweep 2026-10-20 saw it rendered as the visitor's own turn).
      void sendChatMessage('(I just booked the meeting.)', { fromVoice: false, hidden: true });
    }
  });

  // Replay any one-shot pending chat message queued by a previous-page
  // inline link (sp:nav?url=...&msg=...). The previous page persisted
  // the message + navigated; we dispatch now so the agent's reply
  // arrives in this page's context. Cleared from the snapshot below
  // so a refresh doesn't double-fire.
  if (snapshot.pendingMessage && !config.mock) {
    const pendingText = snapshot.pendingMessage.text;
    const pendingHidden = snapshot.pendingMessage.hidden === true;
    // A live voice call owns the arrival (the bot speaks on the new page
    // itself); the typed direction is only for text sessions.
    if (pendingMessageSendable(snapshot.pendingMessage, snapshot.voiceModeActive)) {
      setTimeout(() => {
        void sendChatMessage(pendingText, { fromVoice: false, hidden: pendingHidden });
      }, 200);
    }
    persist({
      publicId: config.publicId,
      sessionId: snapshot.sessionId,
      history: snapshot.history,
      pendingHighlight: snapshot.pendingHighlight,
      // Consumed: explicit null clears it (undefined would now keep it).
      pendingMessage: null,
      voiceModeActive: snapshot.voiceModeActive,
    });
  }

  // Inline agent-link click handler — intercepts <a href="sp:nav?...">
  // links rendered by the markdown pipeline. Parses url + msg, queues
  // the message for the next page, then navigates. Same-origin only;
  // cross-origin or msg-only links are degraded to plain message
  // dispatch without nav.
  ui.root.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const anchor = target?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href') ?? '';
    if (!href.startsWith('sp:')) return;
    e.preventDefault();
    // sp:nav?url=/saas&msg=Tell+me+about+SaaS or sp:?msg=...
    const queryStart = href.indexOf('?');
    if (queryStart === -1) return;
    const params = new URLSearchParams(href.slice(queryStart + 1));
    const url = params.get('url')?.trim() ?? '';
    const msg = params.get('msg')?.trim() ?? '';
    if (!msg && !url) return;
    if (url) {
      try {
        const target = new URL(url, window.location.href);
        // www-tolerant + host-rebased: a KB link carrying the other
        // host variant must NOT open a new tab (fresh sessionStorage =
        // conversation lost). Rebase onto the current origin so the
        // snapshot survives. Genuinely cross-site still falls through
        // to the new-tab path below.
        const rebased = rebaseToVisitorOrigin(target, window.location);
        if (rebased) {
          // Queue the message + nav. The next-page widget reads
          // pendingMessage and dispatches via sendChatMessage above.
          if (msg) {
            persist({
              publicId: config.publicId,
              sessionId,
              history,
              pendingHighlight: undefined,
              pendingMessage: { text: msg, queuedAt: Date.now() },
              voiceModeActive: voiceState.value.name !== 'idle',
            });
          }
          window.location.href = rebased.href;
          return;
        }
        // Cross-origin: open in a new tab, fall through to dispatch
        // the message inline on the current page so the visitor still
        // gets a reply.
        window.open(target.href, '_blank', 'noopener,noreferrer');
      } catch {
        /* malformed url — fall through to msg-only dispatch */
      }
    }
    if (msg) void sendChatMessage(msg, { fromVoice: false });
  });

  // Voice-toggle wiring (Daily + Pipecat path). When voice is on for
  // the agent AND we're not in mock mode, hot-mic via Daily WebRTC.
  // Audio plane runs through the bot's Daily room; tool side-effects
  // arrive over the SSE data plane and feed the same handleChatEvent
  // dispatcher the text path uses.
  if (voiceEnabled && !config.mock) {
    const openVoice = (rejoinRoom?: RejoinRoom | null): void => {
      // Optimistic voice-mode UI so the toggle doesn't sit in a half-
      // state during the ~1-2 sec setup. The state machine handles
      // the inner CONNECTING / LISTENING / SPEAKING transitions; the
      // header badge reflects them.
      ui.setVoiceModeActive(true);
      persist({
        publicId: config.publicId,
        sessionId,
        history,
        voiceModeActive: true,
      });

      // Presenter: voice is taking the stage — the pending text auto-seed
      // stands down (the BOT opens the demo once it joins the room) and
      // the continuation tick goes quiet for the whole call.
      cancelPresenterSeed?.();
      presenterStage?.setVoiceActive(true);
      quietNudge.cancel();

      void startVoice({
        publicId: config.publicId,
        chatApiBaseUrl: config.apiBaseUrl,
        pageUrl: window.location.href,
        // Presenter — the voice bot gets the same PRESENTING stance +
        // stage tools the text runtime gets from context.projectPublicId.
        // Presence law: on site mounts, a tour on the stage at call
        // start rides alone — the server derives its parent hub.
        projectPublicId: config.projectPublicId,
        tourPublicId:
          config.presenterTourPublicId ??
          (config.projectPublicId ? undefined : (ui.activeTourPublicId() ?? undefined)),
        // Presenter door: the server closes stale stage memory and the
        // bot renders the door face.
        presentDoor: config.presentDoor,
        sessionId, // reuse if visitor has typed already — one chat_sessions row across modes
        // Warm reconnect after a full page reload: rejoin the room the prior
        // page was in (bot held alive in its grace window) instead of a cold
        // summon. Only the cross-page auto-reopen passes this; a fresh
        // user-initiated start leaves it undefined.
        rejoinRoom,
        onChatEvent: (event) => {
          // Voice-transcript events come over the SSE data plane
          // (audio via Daily WebRTC, transcripts via the same SSE
          // channel the text path uses). Render as chat bubbles using the
          // same UI methods text mode uses. Each assistant turn is
          // an atomic utterance (TranscriptProcessor aggregates TTS
          // chunks until BotStoppedSpeakingFrame), so we toggle
          // streaming on/off around each one to create a fresh
          // bubble — without the toggle, all turns concatenate into
          // a single bubble because `activeStreaming` never resets.
          if (event.type === 'voice-caption') {
            // Live caption — the utterance so far, paced to her voice.
            // Caption surfaces only (Beam speech card, PiP ticker); the
            // chat log waits for the turn-end transcript below.
            ui.setSpeechCaption(event.payload.text);
            return;
          }
          if (event.type === 'voice-transcript') {
            const { role, text, source } = event.payload;
            // A typed message was painted (and pushed to history) by
            // sendChatMessage before it went to the bot — don't paint twice.
            if (role === 'user' && source === 'typed') return;
            if (role === 'user') {
              // Deepgram emits multiple TranscriptionFrames per utterance
              // (one per pause/chunk), so without grouping the visitor's
              // single thought renders as 5+ separate bubbles. Append to
              // the previous user bubble when fragments arrive close
              // together; start fresh after a >2.5s gap (new utterance)
              // OR after an assistant turn breaks the streak.
              const now = Date.now();
              const FRAGMENT_WINDOW_MS = 2500;
              const last = history[history.length - 1];
              const continuingUser =
                last?.role === 'user' && now - lastUserFragmentAt < FRAGMENT_WINDOW_MS;
              if (continuingUser && last) {
                const joined = (last.content + ' ' + text).trim();
                last.content = joined;
                ui.appendUserDelta(' ' + text);
                ui.finalizeUserMessage(joined);
              } else {
                ui.appendUserMessage(text);
                history.push({ role: 'user', content: text });
              }
              lastUserFragmentAt = now;
            } else {
              ui.handleChatEvent({
                type: 'message',
                payload: { text, partial: false },
              });
              ui.setStreaming(false);
              history.push({ role: 'assistant', content: text });
              // M5 — feed the co-pilot PiP caption ticker with the agent's
              // latest line (no-op unless the PiP is visible).
              ui.setSpeechCaption(text);
            }
            persist({
              publicId: config.publicId,
              sessionId,
              history,
              voiceModeActive: true,
            });
            return;
          }
          ui.handleChatEvent(event);
        },
        onSessionReady: (sid, response) => {
          // Adopt the voice sessionId as the canonical session for
          // this conversation. /chat from this point forward uses
          // the same id, so transcripts (text + voice) interleave
          // server-side under one row.
          sessionId = sid;
          // Remember the room so the NEXT page (after the agent's navigate
          // tool does a full reload) can rejoin it instead of cold-summoning.
          // Cleared on deliberate end (stop toggle / idle / error) below.
          const room: SessionSnapshot['voiceRoom'] =
            response.roomName && typeof response.roomExpiresAt === 'number' && response.roomSig
              ? {
                  roomName: response.roomName,
                  roomUrl: response.roomUrl,
                  expiresAt: response.roomExpiresAt,
                  sig: response.roomSig,
                }
              : undefined;
          persist({
            publicId: config.publicId,
            sessionId,
            history,
            voiceModeActive: true,
            voiceRoom: room ?? null,
          });
          // Seed initial page-context so the bot's first turn knows
          // what's on screen for highlight + context-aware answers.
          // Voice tools can't see the page DOM otherwise — text mode
          // gets pageElements in every chat send; voice doesn't have
          // an equivalent transport, hence this dedicated push.
          void pushVoicePageContext({
            chatApiBaseUrl: config.apiBaseUrl,
            sessionId: sid,
            publicId: config.publicId,
            url: window.location.href,
            pageElements: scrapePageElements(),
            // E8 — a fresh call from a page that already greeted: tell the
            // bot what the visitor read so its first line picks up from it
            // instead of greeting again. A resumed thread carries history
            // and needs no hint.
            surfaceGreeting:
              history.length === 0 ? (proactiveGreeting ?? (welcomeMessage || null)) : null,
          });
          // Warm-rejoin watchdog: when the server reconnected us to an
          // EXISTING room, the bot should already be there (grace window).
          // If its grace had elapsed (slow load / crash) the room is empty
          // and the state never leaves CONNECTING — detect that and
          // cold-retry ONCE with a fresh room + bot. A cold start
          // (reused=false) skips this; its own summon timeout handles a
          // no-show bot.
          if (response.reused) {
            // The lingering bot's LLM context is frozen on the PRE-nav page —
            // it spawned there and never saw the navigation. Tell it where the
            // visitor actually is now (same channel SPA soft-nav uses) so
            // page-aware answers + highlight target the NEW page and it acks
            // arrival instead of insisting it's still on the old one. The
            // delay lets the page-context push (above) land and the rejoin
            // settle so the bot's page-changed handler fetches fresh elements.
            setTimeout(() => {
              if (voiceHandle) notifyVoiceOfPageChange(window.location.href);
            }, 700);
            // Warm-rejoin watchdog: if the bot's grace had already elapsed the
            // room is empty and the state never leaves CONNECTING — cold-retry
            // once with a fresh room + bot.
            setTimeout(() => {
              if (voiceHandle && voiceState.value.name === 'connecting') {
                console.warn('[showpilot] warm rejoin found no bot — cold retry');
                const handle = voiceHandle;
                voiceHandle = null;
                void handle.close().then(() => openVoice(null));
              }
            }, 5000);
          }
        },
        onSessionClosed: (info) => {
          // Presenter: the call ended — text pacing may resume.
          presenterStage?.setVoiceActive(false);
          voiceHandle = null;
          const isError = voiceState.value.name === 'error';
          ui.setVoiceModeActive(false);
          if (info.reason) {
            // The bot ended the call as an error (dropped, respawn failed).
            ui.showError(info.reason);
          } else if (isError) {
            ui.showError(voiceState.value.errorMessage ?? "Voice — couldn't connect.");
          }
          // A closed session ALWAYS clears voice continuity. The old
          // "keep true for page-unload" carve-out was dead code — this
          // callback never runs during an unload (the page is gone);
          // nav continuity is persisted BEFORE unload by persist()'s
          // preserve-on-omit semantics. What the carve-out actually did
          // was race the stop-toggle's explicit false: close() resolves
          // async and re-persisted true AFTER it, so a reload from a
          // deliberately ended call auto-opened video (David prod
          // 2026-07-16). Pre-#149 the stale true got accidentally wiped
          // by later persists; the preserve semantics un-masked it.
          persist({
            publicId: config.publicId,
            sessionId,
            history,
            voiceModeActive: false,
            voiceRoom: null,
          });
        },
      })
        .then((handle) => {
          voiceHandle = handle;
        })
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'voice setup failed';
          console.warn('[showpilot] voice open failed', err);
          ui.showError(`Voice — ${message}`);
          ui.setVoiceModeActive(false);
          persist({
            publicId: config.publicId,
            sessionId,
            history,
            voiceModeActive: false,
            voiceRoom: null,
          });
        });
    };

    ui.onVoiceToggle((next) => {
      if (next === 'stop') {
        const handle = voiceHandle;
        voiceHandle = null;
        void handle?.close();
        ui.setVoiceModeActive(false);
        persist({
          publicId: config.publicId,
          sessionId,
          history,
          voiceModeActive: false,
          // Deliberate hang-up — forget the room so the next page starts
          // fresh rather than reconnecting to a call the visitor ended.
          voiceRoom: null,
        });
        return;
      }
      openVoice();
    });

    // Cross-page voice continuity: if the prior page was mid-call when
    // it navigated, auto-reopen on this page. The conversation history
    // is already seeded into the new session via the persisted
    // sessionId; the bot's system prompt re-loads on agent-config
    // fetch so context picks up cleanly. When the prior page also stashed
    // its room, ask to REJOIN it (bot held alive in its grace window) for a
    // ~1s reconnect instead of a full cold summon; the watchdog above
    // cold-falls-back if the bot's grace had already elapsed.
    if (snapshot.voiceModeActive && sessionId) {
      setTimeout(() => openVoice(snapshot.voiceRoom ?? null), 500);
    } else if (config.voiceAutoStart) {
      // Live delivery: the shell's Join click was the gesture — start the
      // call now; the bot joins the room and OPENS the demo (its connect
      // seed). A short beat lets the FULL viewer paint first.
      setTimeout(() => openVoice(), 400);
    }

    // SPA navigation watcher — many host sites (Next.js, React Router,
    // SvelteKit, etc.) navigate via pushState without unloading the
    // page. The widget DOM stays mounted, the Daily call stays alive,
    // but the bot's currentPageUrl is frozen at spawn time. We watch
    // pushState / replaceState / popstate and ping the bot when the URL
    // changes during an active voice call. Watcher also pushes the
    // fresh pageElements snapshot to chat-api so the bot's next LLM
    // turn can highlight / answer based on what's on screen.
    installSpaNavWatcher({
      chatApiBaseUrl: config.apiBaseUrl,
      publicId: config.publicId,
      getSessionId: () => sessionId,
      notifyPageChange: () => ui.notifyPageChange(),
    });
  }
}

let spaNavInstalled = false;
type SpaNavWatcherDeps = {
  chatApiBaseUrl: string;
  publicId: string;
  /** Late-bound sessionId getter — voice session may attach after watcher install. */
  getSessionId: () => string | null;
  /**
   * Notify the widget UI of any page change (voice OR text mode).
   * Used by AgentLinks to re-evaluate {feature} substitution against
   * the new page's `<meta name="agent-feature">` tag.
   */
  notifyPageChange: () => void;
};
let spaNavDeps: SpaNavWatcherDeps | null = null;

function installSpaNavWatcher(deps: SpaNavWatcherDeps): void {
  // The deps reference may need to update across re-mounts (sessionId
  // gets minted late). Latest call wins so the watcher's closure
  // doesn't capture a stale sessionId.
  spaNavDeps = deps;
  if (spaNavInstalled || typeof window === 'undefined') return;
  spaNavInstalled = true;

  let lastHref = window.location.href;
  const fire = (): void => {
    const href = window.location.href;
    if (href === lastHref) return;
    lastHref = href;
    visitPageChanged(href);

    // Always notify the widget UI — AgentLinks re-evaluates {feature}
    // against the new page's meta tag whether or not voice is active.
    spaNavDeps?.notifyPageChange();

    // Slice 6.6 SPA nav re-trigger — re-fire /v1/decide so the engage
    // greeter (or future per-page surfaces) can re-personalize for the
    // new page. Custom event keeps the watcher decoupled from /decide
    // wiring; the init() handler subscribes.
    window.dispatchEvent(new CustomEvent('showpilot:spa-nav', { detail: { href } }));

    // Voice-only signals: skip when no active voice call.
    if (voiceState.value.name === 'idle') return;
    notifyVoiceOfPageChange(href);
    // Push the fresh page-elements snapshot so the bot can highlight
    // / answer about the new page. Fire-and-forget; the bot reads it
    // on its next LLM turn.
    const sid = spaNavDeps?.getSessionId() ?? null;
    if (sid && spaNavDeps) {
      void pushVoicePageContext({
        chatApiBaseUrl: spaNavDeps.chatApiBaseUrl,
        sessionId: sid,
        publicId: spaNavDeps.publicId,
        url: href,
        pageElements: scrapePageElements(),
      });
    }
  };

  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);
  history.pushState = function (...args) {
    const r = origPush(...args);
    fire();
    return r;
  };
  history.replaceState = function (...args) {
    const r = origReplace(...args);
    fire();
    return r;
  };
  window.addEventListener('popstate', fire);
}

/**
 * Set after mountWidget so persist() can stamp every snapshot with the
 * layout the visitor currently has open (cross-page resume fidelity).
 */
let currentLayoutForPersist: (() => string) | null = null;

function persist(args: {
  publicId: string;
  sessionId: string | null;
  history: ChatHistoryMessage[];
  pendingHighlight?: SessionSnapshot['pendingHighlight'];
  /** `undefined` keeps the stored one, an object sets it, `null` clears it (same rule as voiceRoom). */
  pendingMessage?: SessionSnapshot['pendingMessage'] | null;
  voiceModeActive?: boolean;
  voiceRoom?: SessionSnapshot['voiceRoom'] | null;
}): void {
  // voiceRoom is needed by only a few call sites (voice session-ready sets
  // it; deliberate voice-end clears it) but it must SURVIVE the many other
  // persist calls that fire mid-call (transcripts, cross-nav handoff). So
  // default to preserving whatever's stored: `undefined` keeps it, an
  // explicit object sets it, explicit `null` clears it.
  const stored = loadSession(args.publicId);
  const voiceRoom = args.voiceRoom === undefined ? stored.voiceRoom : (args.voiceRoom ?? undefined);
  // Same preserve-on-omit rule for voiceModeActive. Without it, any
  // persist call that omits the flag silently kills voice continuity —
  // the onBeforeHostNavigate snapshot did exactly that, so every
  // AGENT-DRIVEN navigate wiped the flag mid-call and the next page
  // never rejoined (David prod 2026-07-15: nav → dead call, no video,
  // bot's 20s grace expired). The highlight-nav path was hand-patched
  // for this same bug once already — fix the semantics, not call sites.
  const voiceModeActive =
    args.voiceModeActive === undefined ? stored.voiceModeActive : args.voiceModeActive;
  // pendingMessage gets the preserve-on-omit rule too: the arrival
  // direction is queued by onBeforeHostNavigate while the navigating turn
  // is still streaming, and that turn's end-of-stream persist (which never
  // mentions pendingMessage) must not wipe it before the page unloads
  // (prod 2026-10-18: direction persisted, then overwritten, never sent).
  const pendingMessage =
    args.pendingMessage === undefined ? stored.pendingMessage : (args.pendingMessage ?? undefined);
  const snapshot: SessionSnapshot = {
    sessionId: args.sessionId,
    history: args.history,
    pendingHighlight: args.pendingHighlight,
    pendingMessage,
    voiceModeActive,
    voiceRoom,
    layout: currentLayoutForPersist?.(),
    updatedAt: Date.now(),
  };

  // TEMPORARY FIX: When the agent loads with persisted session values, booting directly into 'full'
  // layout causes issues (such as minimizing falling back unexpectedly to site-agent layout).
  // Overriding 'full' to 'sidebar' ensures the agent re-opens cleanly in sidebar posture on page load.
  if (snapshot.layout === 'full') {
    snapshot.layout = 'sidebar';
  }

  saveSession(args.publicId, snapshot);
}

function safeVisitorId(): string | undefined {
  try {
    return getOrCreateVisitorFingerprint();
  } catch {
    return undefined;
  }
}

function initTours(): void {
  if (typeof document === 'undefined') return;

  transformLegacyTours();

  const claim = (sel: string) => {
    const found = Array.from(document.querySelectorAll<HTMLElement>(sel)).filter(
      (m) => !m.getAttribute('data-showpilot-tour-initialized'),
    );
    // Claim the mounts synchronously so a second initTours() during the
    // in-flight import can't double-boot them.
    found.forEach((m) => m.setAttribute('data-showpilot-tour-initialized', 'true'));
    return found;
  };
  const mounts = claim('[data-showpilot-tour]');
  const projects = claim('[data-showpilot-project]');
  // No tour on this page — never fetch the engine. This is the whole point
  // of the split: most pages running the chat widget host no tour at all.
  if (mounts.length === 0 && projects.length === 0) return;

  // Visitor fingerprint rides along so public-player events stitch to the
  // site visit (the tours chunk must not import visitor-id itself).
  const ctx = { visitorId: safeVisitorId() };
  const release = (list: HTMLElement[]) =>
    list.forEach((m) => m.removeAttribute('data-showpilot-tour-initialized'));
  if (mounts.length > 0) {
    void loadTours()
      .then((bootTour) => mounts.forEach((mount) => bootTour(mount, ctx)))
      .catch((err: unknown) => {
        // Release the claim so a later init can retry after a transient failure.
        release(mounts);
        console.error('[showpilot] tour viewer failed to load', err);
      });
  }
  if (projects.length > 0) {
    void loadProject()
      .then((bootProject) => projects.forEach((mount) => bootProject(mount, ctx)))
      .catch((err: unknown) => {
        release(projects);
        console.error('[showpilot] project player failed to load', err);
      });
  }
}

function init(): void {
  if (typeof document === 'undefined') return;
  // Internal QA: dev-widget channel override (?sp_widget=<channel>).
  // When active, THIS bundle defers entirely to the channel bundle it
  // injects — one widget instance per page, never two. The channel
  // bundle runs this same init and boots normally (recursion guard in
  // dev-widget.ts). onerror falls back to booting /v1 late.
  window.__spWidgetFallbackBoot = initInner;
  applyWidgetChannelFromUrl();
  if (maybeLoadWidgetChannel()) return;
  initInner();
}

function initInner(): void {
  // Internal QA: process ?sp_env/?sp_api once and (if active) show the
  // dev/prod toggle pill. No-op for customers, who never set the param.
  applyEnvOverrideFromUrl();
  renderDevEnvPill();
  // Action Links: one delegated click listener for `data-showpilot-action`
  // buttons, and the `#action=<id>` deep link, consumed once. Both queue
  // until a mount binds (and mount the widget themselves on pages whose
  // triggers would otherwise defer it).
  installActionClicks(document, (id) => requestAction(id, 'page'));
  installHashActionListener(window, (id) => requestAction(id, 'hash'));
  const consumeNow = (): void => {
    const id = consumeHashAction(window);
    if (id) requestAction(id, 'hash');
  };
  consumeNow();
  // Host frameworks (a Next.js site, for one) restore the original URL,
  // hash included, when their router hydrates — a replaceState, so no
  // hashchange fires, and the timing varies with the page. Look again
  // every second for the first ten; the runner treats the same Action
  // twice as one open, so a restored hash never double-fires.
  let looks = 0;
  const again = window.setInterval(() => {
    consumeNow();
    if (++looks >= 10) window.clearInterval(again);
  }, 1000);
  // Tour-only pages (the public tour page, a launcher on a site with no
  // agent) boot tours BEFORE any agent mount resolves the config, so the
  // lazy chunk base must be set here or loadTours() rejects with "tours
  // chunk base not configured". The agent path re-sets it from config.
  setToursChunkBase(detectAssetBaseUrl());
  initTours();
  const mounts = document.querySelectorAll<HTMLElement>(MOUNT_SELECTOR);
  // If no explicit mount element exists but a `?ptkn=<id>` URL token is
  // present, auto-create a hidden mount so the widget still boots —
  // matches the legacy URL-token entry point used for share links.
  if (mounts.length === 0 && typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    if (params.get('ptkn')) {
      autoMount(params.get('ptkn')!, 'sidebar');
      return;
    }
    // The public tour page hosts the tour and nothing else — discovery
    // would mount whichever agent claims the share host, which is never
    // what a shared tour link means.
    if (window.SHOWPILOT_CONFIG?.tourPage) return;
    // Universal-embed path — no `data-showpilot-agent` and no `?ptkn=`.
    // Ask the server which agent (if any) owns this host. See Slice B2.
    void discoverAndMount();
    return;
  }
  // Surface duplicate-mount hazards loudly. Two mount nodes on the
  // same page = two independent widget instances, two voice SSE
  // streams, etc. — symptoms include "the agent says it called the
  // tool but the visitor sees nothing happen" because the side-effect
  // event lands on the wrong instance's stream. Customers occasionally
  // ship legacy attrs alongside the v3 attr on different containers;
  // we warn so it's findable in the console.
  if (mounts.length > 1) {
    console.warn(
      `[showpilot] ${mounts.length} mount elements detected on this page. ` +
        `Multiple widget instances will boot — voice tool side-effects may ` +
        `land on the wrong instance. Mount attributes:`,
      Array.from(mounts).map((m) =>
        Array.from(m.attributes)
          .filter((a) => a.name.includes('showpilot') || a.name.includes('storyscale'))
          .map((a) => `${a.name}="${a.value}"`)
          .join(' '),
      ),
    );
  }
  mounts.forEach((mount) => {
    if (mount.getAttribute('data-showpilot-initialized')) return;
    mount.setAttribute('data-showpilot-initialized', 'true');
    void boot(mount);
  });
}

/**
 * View → boot layout map. Used by discover-driven mounts so the widget
 * has SOMETHING to render before /v1/agents/:publicId/config returns
 * the canonical view. setView() then takes over once /config lands.
 */
const VIEW_BOOT_LAYOUTS: Record<string, string> = {
  'site-agent': 'input',
  'chat-button': 'icon',
  engage: 'engage',
  rail: 'sidebar',
  // Unbound boots on the icon layout (dock) — the keyed stage mounts
  // over it once /config delivers the plates.
  unbound: 'icon',
};

/**
 * Append a hidden mount element with the given publicId + boot layout,
 * then boot the widget on it. Used both by the `?ptkn=` legacy entry
 * point and by the universal-embed discover path.
 *
 * The second arg is either a view name (preferred, from /v1/discover)
 * or a literal legacy layout string (for the share-link `?ptkn=` path
 * that hard-codes `sidebar`). View names are mapped to their boot
 * layout; bare layouts pass through.
 */
function autoMount(publicId: string, viewOrLayout: string, position?: 'right' | 'center'): void {
  const auto = document.createElement('div');
  auto.setAttribute('data-showpilot-agent', publicId);
  const layout = VIEW_BOOT_LAYOUTS[viewOrLayout] ?? viewOrLayout;
  if (isLayout(layout)) auto.setAttribute('data-showpilot-layout', layout);
  // Carry the VIEW itself too — views without a 1:1 legacy layout
  // (unbound ⇒ icon would round-trip to chat-button otherwise).
  if (viewOrLayout in VIEW_BOOT_LAYOUTS) auto.setAttribute('data-showpilot-view', viewOrLayout);
  // Presence anchor authored on the trigger (New Trigger modal) — the
  // same attr the embed snippet supports, so config.ts owns the parse.
  if (position === 'center') auto.setAttribute('data-showpilot-position', 'center');
  auto.style.display = 'contents';
  document.body.appendChild(auto);
  auto.setAttribute('data-showpilot-initialized', 'true');
  void boot(auto);
}

/**
 * Internal-only env toggle pill. Renders ONLY when a ShowPilot staffer has
 * activated internal mode (via `?sp_env=dev` etc.) in this browser — so it
 * is never visible to customers. Click = flip dev<->prod (reloads so the
 * whole widget re-points). Right-click = exit internal mode. Fully
 * inline-styled so it can't collide with or disturb the host page.
 */
function renderDevEnvPill(): void {
  if (typeof document === 'undefined' || !isInternalMode()) return;
  if (document.getElementById('sp-dev-env-pill')) return;
  const onDev = getApiBaseOverride() !== null;
  const pill = document.createElement('button');
  pill.id = 'sp-dev-env-pill';
  pill.type = 'button';
  pill.textContent = `${onDev ? '● DEV' : '○ PROD'} · ShowPilot`;
  pill.title = 'ShowPilot internal env toggle — click to flip dev/prod, right-click to exit';
  pill.style.cssText =
    // Top-left: the bottom edge belongs to the widget (start bar's greeter
    // circle sat exactly under this pill in every mobile test screenshot).
    'position:fixed;top:12px;left:12px;z-index:2147483647;margin:0;' +
    'padding:6px 10px;border:0;border-radius:999px;cursor:pointer;color:#fff;' +
    'font:600 12px/1 ui-monospace,Menlo,monospace;letter-spacing:.02em;' +
    'box-shadow:0 2px 8px rgba(0,0,0,.25);background:' +
    (onDev ? '#b91c1c' : '#334155');
  pill.addEventListener('click', () => {
    setBackendOverride(onDev ? null : SP_DEV_API_BASE);
    window.location.reload();
  });
  pill.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    exitInternalMode();
    pill.remove();
    window.location.reload();
  });
  document.body.appendChild(pill);
}

/**
 * Universal-embed bootstrap. Customer site ships only:
 *   <script async src="https://cdn.showpilot.com/widget/v1/app.js"></script>
 * No agent UUID, no mount div. We call `/v1/discover` with the current
 * host + path, and if exactly one agent claims this host (per its org's
 * targeting_rules.origins), we mount it.
 *
 * Failure modes:
 *   - 404 (no rule matches the host) → silent no-op; visitor sees nothing
 *   - 409 (multiple orgs claim the host) → console.warn; site needs an
 *     explicit data-showpilot-agent to disambiguate
 *   - network error → console.warn; no widget renders
 */
async function discoverAndMount(): Promise<void> {
  const overrideBase =
    getApiBaseOverride() ??
    (typeof window !== 'undefined' ? window.SHOWPILOT_CONFIG?.apiBaseUrl : undefined);
  const apiBaseUrl: string = overrideBase ?? 'https://api.showpilot.com';
  const host = window.location.hostname;
  const path = window.location.pathname || '/';
  if (!host) return;
  // Offers v2 — operator preview link. A signed sp_offer_preview token
  // makes discover return THAT offer unconditionally (server-verified).
  const previewToken =
    new URLSearchParams(window.location.search).get('sp_offer_preview') ?? undefined;
  const result = await discoverAgentForHost(
    apiBaseUrl,
    host,
    path,
    undefined,
    previewToken,
    getOrCreateVisitorFingerprint(),
    // Draft preview (publish model): read at boot — this runs BEFORE
    // any mount config exists — so the previewed agent force-mounts on
    // whichever org site the operator opened (server verifies sig+org).
    readAgentPreviewToken(),
  );
  switch (result.kind) {
    case 'matched': {
      // PRESENCE-WINS (Beam): she was on stage earlier in this visit for
      // this agent → mount her now, ahead of this page's own view, its
      // deferred triggers, or an arrival offer. /config re-checks the
      // serving gate before the stage actually mounts.
      if (beamPresenceFor(sessionStorageOrNull(), result.agentPublicId)) {
        autoMount(result.agentPublicId, 'unbound');
        break;
      }
      // Offers v2 — evaluate the compiled live offer deploys for this
      // arrival. The server matched pages + the date window; the utm
      // condition is ours (the server never sees the query string).
      // First match wins, interstitials ahead of toasts/banners; the
      // stash is presented by boot() once /config resolves.
      if (result.arrivalOffers?.length) {
        offerIdentity = result.offerIdentity ?? null;
        const params = new URLSearchParams(window.location.search);
        // Device condition — authoring is Desktop / Mobile; the phone
        // class is 'mobile', tablet rides with desktop.
        const deviceClass = deviceSig.value === 'phone' ? 'mobile' : 'desktop';
        // Referrer host (suffix match) + first-seen marker for the
        // new/returning condition — read before write.
        let refHost = '';
        try {
          refHost = new URL(document.referrer).hostname.toLowerCase().replace(/^www\./, '');
        } catch {
          /* no referrer */
        }
        let returning = false;
        try {
          returning = localStorage.getItem('sp_seen') === '1';
          localStorage.setItem('sp_seen', '1');
        } catch {
          /* storage unavailable — treat as new */
        }
        const eligible = result.arrivalOffers.filter((a) => {
          const c = a.conditions;
          if (c?.utm && params.get(c.utm.property) !== c.utm.value) return false;
          if (c?.referrerHosts?.length) {
            if (!refHost) return false;
            if (!c.referrerHosts.some((r) => refHost === r || refHost.endsWith('.' + r)))
              return false;
          }
          if (c?.visitorType && (c.visitorType === 'returning') !== returning) return false;
          const devices = c?.devices;
          return !devices || devices.length === 0 || devices.includes(deviceClass);
        });
        pendingArrivalOffer =
          eligible.find((a) => a.presentation === 'interstitial') ?? eligible[0] ?? null;
        // A matched arrival offer IS the landing — mount now with the
        // top-level view rather than waiting on a deferred trigger
        // (a campaign visitor must not scroll to earn their invite).
        if (pendingArrivalOffer) {
          autoMount(result.agentPublicId, result.view);
          break;
        }
      }
      // No triggers configured for this path → mount immediately. This
      // covers domain-only allowlists imported from legacy and orgs
      // that haven't yet wired up Path/Trigger rules.
      if (!result.triggers || result.triggers.length === 0) {
        // Phase 3 — auto-open etiquette (rail presence): once per session;
        // a visitor who dismissed the rail stays docked for the rest of the
        // session. The marker is only ever written by flag-on widgets
        // (ui.tsx close handler), so this is transitively behind ui.mobileV2.
        let bootView = result.view;
        try {
          if (
            result.autoOpen === 'desktop-tablet' &&
            sessionStorage.getItem('sp_rail_dismissed') === '1'
          ) {
            bootView = 'chat-button';
          }
        } catch {
          /* storage unavailable — keep the authored view */
        }
        autoMount(result.agentPublicId, bootView);
        break;
      }
      // Triggers configured → defer mount until a trigger fires. The
      // watcher tears itself down on first fire.
      const watcher = installTriggerWatchers(result.triggers as WatcherTrigger[], (t) => {
        activeDeploymentId = t.deploymentId ?? null;
        autoMount(t.agentPublicId, t.view, t.meta?.position);
      });
      // An Action Link is its own trigger: a click, deep link or SDK call
      // mounts her now instead of waiting for scroll or dwell.
      setMountOnDemand(() => {
        watcher.cleanup();
        autoMount(result.agentPublicId, result.view);
      });
      // Also tear down on page unload so we don't leak listeners across
      // SPA-style navigation.
      const onPageHide = (): void => watcher.cleanup();
      window.addEventListener('pagehide', onPageHide, { once: true });
      break;
    }
    case 'ambiguous':
      console.warn(
        '[showpilot] /v1/discover: this host matches multiple orgs; ' +
          'set <div data-showpilot-agent="<UUID>"> to disambiguate.',
      );
      break;
    case 'none': {
      // 404 — no targeting rule allowlists this host/path. Silent: the
      // visitor sees nothing rather than an error — unless she is already
      // on stage in this visit: presence carries onto rule-less pages too.
      const presence = readBeamPresence(sessionStorageOrNull());
      if (presence) autoMount(presence.agentPublicId, 'unbound');
      break;
    }
    case 'error':
      console.warn(`[showpilot] /v1/discover failed (status ${result.status})`);
      break;
  }
}

/**
 * Local date/time string for the prompt's "# Current Date and Time" block —
 * weekday spelled out so the model never derives day-of-week (it gets that
 * wrong from a bare ISO date), in the visitor's own timezone. ~34 chars, well
 * inside the runtime-contract 64-char cap. Falls back to ISO if Intl throws.
 */
function formatLocalDateTime(): string {
  try {
    return new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return new Date().toISOString();
  }
}

/**
 * Pull the embed's arrival touch (full utm_*, scrubbed landing params
 * incl. click IDs, referrer) for /decide + session create. Cheap — runs
 * once per boot. Parsing + the scrub contract live in `page-context.ts`
 * (pure, tested); bounds there match the server's zod caps.
 */
/** The hidden stage direction the next page sends after an agent-driven navigation. */
export function arrivalDirection(pathname: string): string {
  return `(you just brought the visitor to ${pathname || '/'} — in one line say what is on this page and carry the thread on; no greeting, no re-introduction.)`;
}

function readPageContext(): PageTouch {
  try {
    return parsePageTouch(window.location.search, document.referrer);
  } catch {
    return {};
  }
}

if (typeof document !== 'undefined') {
  // `window.showpilot` exists from the moment the bundle runs; boot binds it.
  installPageSdk();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

export { init, boot, mountWidget };
export type { WidgetUI };
